import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
 HiOutlinePlus,
 HiOutlinePencil,
 HiOutlineTrash,
 HiOutlineExclamation,
 HiOutlineCheckCircle,
 HiOutlineEye,
 HiOutlineChevronLeft,
 HiOutlineX,
 HiOutlineUpload,
 HiOutlineDownload,
 HiOutlineDocumentText,
 HiOutlineSearch,
} from "react-icons/hi";
import TiptapEditor, { renderTiptapHtml, TiptapContentStyles } from "../components/TiptapEditor";
import Pagination from "../components/Pagination";
import { useDebounce } from "../hooks/useDebounce";
import SearchableDropdown from "../components/SearchableDropdown";
import FilterDropdown from "../components/FilterDropdown";
import {
 fetchCycles,
 fetchCycle,
 fetchCycleSchools,
 createCycle,
 updateCycle,
 publishCycle,
 unpublishCycle,
 completeCycle,
 deleteCycle,
 fetchAssessmentYears,
 fetchMasterData,
 fetchHolidays,
 addCycleReminder,
 updateCycleReminder,
 listCycleReminders,
 deleteCycleReminder,
 fetchCompetenciesByGrade,
 fetchCompetencyCategoryMap,
 fetchAllSetsLite,
 fetchSchools,
 previewNonAdoptedSchools,
 createNonAdoptedBatch,
 listStudentCategories,
 createStudentCategory,
 updateStudentCategory,
 deleteStudentCategory,
 fetchStudentsByCategory,
 exportStudentsCategoryCsv,
 fetchPreviousRoundStudents,
} from "../services/masterDataApi";
import Toast from "../components/Toast";
import { apiErrorMessage } from "../utils/apiError";
import { validateReminderInput } from "../utils/reminderValidation";
import { useUserRole } from "../hooks/useUserRole";

// Placeholder grades from config — in production these come from config service
const CONFIG_GRADES = [
 "Grade 1", "Grade 2", "Grade 3", "Grade 4", "Grade 5",
 "Grade 6", "Grade 7", "Grade 8", "Grade 9", "Grade 10",
 "Grade 11", "Grade 12",
];

const EMPTY_FORM = {
 state_id: "",
 round_number: "",
 title: "",
 introduction: "",
 pm_po_notes: "",
 private_notes: "",
 start_date: "",
 end_date: "",
 holiday_calendar_id: "",
 assessment_year_id: "",
 grades: [], // [{grade_code, competencies:[{competency_id}]}]
 school_ids: [], // non-adopted school IDs (for quarterly cycles)
};

/* ── shared input classes (white bg, proper border, matching Mappings) ── */
const selectClass =
 "w-full bg-white border border-[#d0d7de] rounded-lg px-3 py-2 text-sm text-[#24292f] focus:outline-none focus:ring-2 focus:ring-[#0078d4]/20 focus:border-[#0078d4] hover:border-[#0078d4]/40 transition-colors appearance-none";
const inputClass =
 "w-full bg-white border border-[#d0d7de] rounded-lg px-3 py-2 text-sm text-[#24292f] focus:outline-none focus:ring-2 focus:ring-[#0078d4]/20 focus:border-[#0078d4] hover:border-[#0078d4]/40 transition-colors";

/* ── client-side pagination page size for the quarterly cycle "Schools" table ── */
const SCHOOL_PAGE_SIZE = 10;

// Helper: paginated school chips for the detail view (quarterly cycles only).
// Fed by GET /commons/cycles/{id}/schools rather than by the cycle detail
// response, which carries only the count. A continuous cycle covers every
// adopted school in its state (~141k for UP), so lists stay paginated.
const DetailCycleSchools = ({ cycleId, totalHint }) => {
 const SCHOOLS_PAGE_SIZE = 20;
 const [rows, setRows] = useState([]);
 const [page, setPage] = useState(1);
 const [totalCount, setTotalCount] = useState(totalHint || 0);
 const [totalPages, setTotalPages] = useState(1);
 const [search, setSearch] = useState("");
 const [loading, setLoading] = useState(false);
 const debouncedSearch = useDebounce(search, 400);

 // A new search term restarts paging; otherwise page 3 of the old term would
 // be requested for the new one.
 useEffect(() => { setPage(1); }, [debouncedSearch]);

 useEffect(() => {
  let cancelled = false;
  setLoading(true);
  fetchCycleSchools(cycleId, { page, page_size: SCHOOLS_PAGE_SIZE, search: debouncedSearch })
   .then((r) => {
    if (cancelled) return;
    setRows(r.data || []);
    setTotalCount(r.total_count || 0);
    setTotalPages(r.total_pages || 1);
   })
   .catch(() => { if (!cancelled) setRows([]); })
   .finally(() => { if (!cancelled) setLoading(false); });
  return () => { cancelled = true; };
 }, [cycleId, page, debouncedSearch]);

 return (
  <div>
   {/* A cycle that fits on one page renders exactly as it always did: chips and
       nothing else. Search only appears once there is more than a page to sift. */}
   {(totalCount > SCHOOLS_PAGE_SIZE || search) && (
    <input
     type="text"
     placeholder="Search schools by name, UDISE, block or district…"
     value={search}
     onChange={(e) => setSearch(e.target.value)}
     className="w-full mb-3 bg-[#f6f8fa] border border-[#d0d7de] rounded-lg px-3 py-2 text-xs text-[#24292f] focus:outline-none focus:ring-1 focus:ring-[#0078d4]"
    />
   )}
   {loading ? (
    <div className="text-center py-6 text-xs text-[#8b949e]">
     <div className="inline-block w-4 h-4 border-2 border-[#0078d4] border-t-transparent rounded-full animate-spin mr-2"></div>Loading schools…
    </div>
   ) : rows.length === 0 ? (
    <p className="text-xs text-[#8b949e] italic">
     {debouncedSearch ? "No schools match your search." : "No schools selected."}
    </p>
   ) : (
    <>
     {/* Original chip design, now fed one page at a time instead of the whole
         mapping — the markup is unchanged, only the data source is bounded. */}
     <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto p-1">
      {rows.map((r) => (
       <span key={r.id}
        className="inline-flex items-center gap-1.5 bg-green-50 text-green-800 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-green-200">
        <HiOutlineCheckCircle className="text-green-500 flex-shrink-0" />
        <span>{r.school_name || `School #${r.school_id}`}</span>
       </span>
      ))}
     </div>
     {totalCount > SCHOOLS_PAGE_SIZE && (
      <div className="border border-[#e5e7eb] rounded-lg overflow-hidden mt-3">
       <Pagination
        currentPage={page}
        totalPages={totalPages}
        totalCount={totalCount}
        pageSize={SCHOOLS_PAGE_SIZE}
        onPageChange={setPage}
        entityName="schools"
       />
      </div>
     )}
    </>
   )}
  </div>
 );
};

// A native <input type="number"> still lets the user type "e"/"E"/"+"/"-"/"."
// (valid partial scientific-notation syntax), even though these reminder
// fields only ever want whole positive digits — block those keystrokes so
// the field can't visibly hold a non-numeric character in the first place.
const blockNonDigitKey = (e) => {
 if (e.ctrlKey || e.metaKey || e.altKey) return;
 if (e.key.length === 1 && !/[0-9]/.test(e.key)) {
  e.preventDefault();
 }
};

// mode is stored/sent as a comma-separated string (e.g. "in_app,email").
const MODE_LABELS = { in_app: "In-App", email: "Email" };
const formatReminderMode = (mode) => {
 if (!mode) return "—";
 const arr = Array.isArray(mode) ? mode : String(mode).split(",");
 return arr.map((m) => MODE_LABELS[m.trim()] || m.trim()).filter(Boolean).join(", ") || "—";
};

const cycleLengthInDays = (startDate, endDate) => {
 if (!startDate || !endDate) return null;
 const days = Math.round((new Date(endDate) - new Date(startDate)) / 86400000);
 return days > 0 ? days : null;
};

// Every reminder is presented as a day offset from the cycle start. Rows saved by the
// older three-option form used a different trigger shape, so they are mapped onto the
// same scale — mid_cycle is half the cycle, before_end counts back from the end.
const reminderDayOffset = (r, startDate, endDate) => {
 const total = cycleLengthInDays(startDate, endDate);
 if (r.when_to_check === "mid_cycle") return total != null ? Math.floor(total / 2) : null;
 if (r.when_to_check === "before_end") {
  return total != null && r.when_value != null ? Math.max(0, total - r.when_value) : null;
 }
 return r.when_value ?? null;
};

const describeReminder = (r, startDate, endDate) => {
 const day = reminderDayOffset(r, startDate, endDate);
 const when = day != null ? `After ${day} day${day === 1 ? "" : "s"}` : "After — days";
 const target = r.threshold_value != null ? `${r.threshold_value}%` : "—";
 return `${when} · completion below ${target}`;
};
const OP_SYMBOL = { gte: "≥", gt: ">", lte: "≤", lt: "<", eq: "=" };

// Helper: read-only reminder list for detail view
const DetailReminders = ({ cycleId, inline, startDate, endDate }) => {
 const [items, setItems] = useState([]);
 useEffect(() => {
  listCycleReminders(cycleId).then((r) => setItems(r.data || [])).catch(() => {});
 }, [cycleId]);
 if (items.length === 0) return inline ? <p className="text-xs text-[#8b949e] italic">No reminders configured yet.</p> : null;
 return (
  <div className={inline ? "" : "mb-4"}>
   {!inline && <h3 className="text-sm font-semibold text-[#24292f] mb-3">Reminders</h3>}
   <div className="border border-[#e5e7eb] rounded-lg overflow-hidden">
    <table className="w-full text-xs">
     <thead>
      <tr className="bg-[#f6f8fa]">
       <th className="text-left px-3 py-2">Reminder</th>
       <th className="text-left px-3 py-2">Mode</th>
      </tr>
     </thead>
     <tbody>
      {items.map((r) => (
       <tr key={r.id} className="border-t border-[#f0f0f0]">
        <td className="px-3 py-2">{describeReminder(r, startDate, endDate)}</td>
        <td className="px-3 py-2">{formatReminderMode(r.mode)}</td>
       </tr>
      ))}
     </tbody>
    </table>
   </div>
  </div>
 );
};

// Helper: read-only "Selected Competencies Weightage" reference for detail view
const DetailCompetenciesWeightage = ({ stateId, grades, roundNumber, cycleType }) => {
 const [compCategoryMap, setCompCategoryMap] = useState({});
 useEffect(() => {
  if (stateId) {
   fetchCompetencyCategoryMap(stateId)
    .then((res) => setCompCategoryMap(res.data || {}))
    .catch(() => setCompCategoryMap({}));
  } else {
   setCompCategoryMap({});
  }
 }, [stateId]);

 // Same cumulative-weight fetch the create/edit form uses (see maxPossibleScore
 // above) — round > 1 must show the prior rounds' weight too, since that's
 // what the actual category thresholds are compared against. Tracked
 // alongside a ready flag so the table below can wait for this to resolve
 // instead of painting with previousRoundWeight's 0 default and then jumping
 // once the real value arrives.
 const [previousRoundWeight, setPreviousRoundWeight] = useState(0);
 const [previousRoundWeightReady, setPreviousRoundWeightReady] = useState(false);
 useEffect(() => {
  const prevRound = parseInt(roundNumber, 10) - 1;
  setPreviousRoundWeightReady(false);
  // Continuous-only: Quarterly rounds are scored independently, so there's
  // no carried weight to show here (see the create/edit form's identical
  // gate on this same fetch, above).
  if (cycleType === "continuous" && stateId && prevRound >= 1) {
   fetchPreviousRoundStudents({ state_id: stateId, previous_round: prevRound, cycle_type: cycleType, weightOnly: true })
    .then((res) => setPreviousRoundWeight(res.data?.previous_round_total_weight || 0))
    .catch(() => setPreviousRoundWeight(0))
    .finally(() => setPreviousRoundWeightReady(true));
  } else {
   setPreviousRoundWeight(0);
   setPreviousRoundWeightReady(true);
  }
 }, [stateId, roundNumber, cycleType]);

 const rows = useMemo(() => {
  const out = [];
  (grades || []).forEach((g) => {
   (g.competencies || []).forEach((c) => {
    const code = c.competency_code || c.competency_id;
    const info = compCategoryMap[code];
    if (!info) return;
    out.push({ code, name: c.competency_name || code, category_name: info.category_name, weight: info.weight });
   });
  });
  return out;
 }, [grades, compCategoryMap]);
 const currentRoundWeight = rows.reduce((sum, r) => sum + (r.weight || 0), 0);
 const totalWeight = currentRoundWeight + previousRoundWeight;

 if (rows.length === 0) return null;
 // The container itself renders immediately (no layout jump); only the
 // number/table inside waits for previousRoundWeight, so the total shown
 // is always final — never a current-round-only number that jumps once the
 // fetch above completes.
 return (
  <div className="border border-[#e5e7eb] rounded-lg overflow-hidden mb-4">
   <div className="px-3 py-2 bg-[#f6f8fa] border-b border-[#e5e7eb]">
    <span className="text-xs font-semibold text-[#24292f]">Selected Competencies Weightage</span>
    <span className="text-[11px] text-[#8b949e] ml-2">Use this to decide sensible student-category thresholds.</span>
   </div>
   <div className="p-3">
    {!previousRoundWeightReady ? (
     <p className="text-xs text-[#8b949e] italic py-2">Loading…</p>
    ) : (
     <table className="w-full text-xs border border-[#f0f0f0] rounded">
      <thead>
       <tr className="bg-[#fafbfc]">
        <th className="text-left px-2 py-1.5 font-medium text-[#8b949e]">Competency</th>
        <th className="text-left px-2 py-1.5 font-medium text-[#8b949e]">Category</th>
        <th className="text-right px-2 py-1.5 font-medium text-[#8b949e]">Weight</th>
       </tr>
      </thead>
      <tbody>
       {rows.map((r) => (
        <tr key={r.code} className="border-t border-[#f0f0f0]">
         <td className="px-2 py-1.5 text-[#24292f]">{r.name}</td>
         <td className="px-2 py-1.5 text-[#57606a]">{r.category_name}</td>
         <td className="px-2 py-1.5 text-right text-[#57606a]">{r.weight}</td>
        </tr>
       ))}
      </tbody>
      <tfoot>
       {previousRoundWeight > 0 && (
        <tr className="border-t border-[#f0f0f0] text-[#8b949e]">
         <td className="px-2 py-1.5" colSpan={2}>Previous round weight (carried into cumulative score)</td>
         <td className="px-2 py-1.5 text-right">{previousRoundWeight}</td>
        </tr>
       )}
       <tr className="border-t border-[#e5e7eb] bg-[#fafbfc] font-semibold">
        <td className="px-2 py-1.5 text-[#24292f]" colSpan={2}>
         {previousRoundWeight > 0 ? "Total weight (this round + previous)" : "Total weight"}
        </td>
        <td className="px-2 py-1.5 text-right text-[#24292f]">{totalWeight}</td>
       </tr>
      </tfoot>
     </table>
    )}
   </div>
  </div>
 );
};

// Helper: read-only student categories table for detail view
const DetailStudentCategories = ({ cycleId, inline }) => {
 const [items, setItems] = useState([]);
 useEffect(() => {
  listStudentCategories(cycleId).then((r) => setItems(r.data || [])).catch(() => {});
 }, [cycleId]);
 if (items.length === 0) return inline ? <p className="text-xs text-[#8b949e] italic">No categories yet.</p> : null;
 return (
  <div className={inline ? "" : "mb-4"}>
   {!inline && <h3 className="text-sm font-semibold text-[#24292f] mb-3">Student Categories</h3>}
   <div className="border border-[#e5e7eb] rounded-lg overflow-hidden">
    <table className="w-full text-xs">
     <thead>
      <tr className="bg-[#f6f8fa]">
       <th className="text-left px-3 py-2">Category</th>
       <th className="text-left px-3 py-2">Threshold</th>
       <th className="text-left px-3 py-2">Color</th>
      </tr>
     </thead>
     <tbody>
      {items.map((cat) => (
       <tr key={cat.id || cat.name} className="border-t border-[#f0f0f0]">
        <td className="px-3 py-2 font-semibold">{cat.name}</td>
        <td className="px-3 py-2">{OP_SYMBOL[cat.operator || "gte"]} {cat.threshold}</td>
        <td className="px-3 py-2">
         <span className="inline-block w-4 h-4 rounded" style={{ backgroundColor: cat.color || "#9ca3af" }} />
        </td>
       </tr>
      ))}
     </tbody>
    </table>
   </div>
  </div>
 );
};

function Cycles({ cycleType }) {
 const { canManageCycles } = useUserRole();
 const [toast, setToast] = useState(null);

 // ── URL-based view state (survives refresh) ──
 const [searchParams, setSearchParams] = useSearchParams();
 const view = searchParams.get("view") || "list";
 const urlEditId = searchParams.get("edit");
 const setView = useCallback((v, params = {}) => {
  if (v === "list") {
   setSearchParams({});
  } else if (v === "form" && params.edit) {
   setSearchParams({ view: "form", edit: params.edit });
  } else if (v === "form") {
   setSearchParams({ view: "form" });
  } else {
   setSearchParams({ view: v });
  }
 }, [setSearchParams]);

 // ── list state ──
 const [cycles, setCycles] = useState([]);
 const [loading, setLoading] = useState(false);
 const [listSearch, setListSearch] = useState("");
 const debouncedListSearch = useDebounce(listSearch, 400);
 const [filterState, setFilterState] = useState("");
 const [filterYear, setFilterYear] = useState("");
 const [filterStatus, setFilterStatus] = useState("");
 const [page, setPage] = useState(1);
 const [pageSize, setPageSize] = useState(20);
 const [totalPages, setTotalPages] = useState(1);
 const [totalCount, setTotalCount] = useState(0);

 // ── dropdown data ──
 const [states, setStates] = useState([]);
 const [assessmentYears, setAssessmentYears] = useState([]);
 const [setsLite, setSetsLite] = useState([]); // {id, title, competency_code, competency_state_id, set_type, dependent_set_id}
 const [savedGradeCodes, setSavedGradeCodes] = useState({});
 const [savedStateId, setSavedStateId] = useState(null);
 const [holidayCalendarOptions, setHolidayCalendarOptions] = useState([]);

 // ── form state ──
 const [form, setForm] = useState({ ...EMPTY_FORM });
 const [editingId, setEditingId] = useState(null);
 const [saving, setSaving] = useState(false);
 const [formLoading, setFormLoading] = useState(false);
 const [formErrors, setFormErrors] = useState({});

 // ── detail state ──
 const [detailCycle, setDetailCycle] = useState(null);
 // A published/completed cycle renders as read-only (see isLocked usage in
 // the form view below) — computed here too so effects that only matter for
 // the editable wizard (previous-round students, etc.) can skip work that
 // would never be shown.
 const isLocked = editingId && detailCycle && (detailCycle.is_published || detailCycle.is_completed);

 // ── delete confirm ──
 const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
 // ── publish/unpublish confirm ──
 const [showPublishConfirm, setShowPublishConfirm] = useState(null);
 const [showUnpublishConfirm, setShowUnpublishConfirm] = useState(null);
 // ── complete confirm ──
 const [showCompleteConfirm, setShowCompleteConfirm] = useState(null);

 // ── school selection state (quarterly cycles — non-adopted table) ──
 // A state can hold lakhs of schools, so the table is paged and searched on the
 // server, and the form never holds the cycle's full school list. It keeps only
 // what the user changed; handleSave sends those changes (see CycleSchoolChanges
 // in master_data schemas/cycle.py).
 const [schoolRows, setSchoolRows] = useState([]); // current server page
 const [schoolTotal, setSchoolTotal] = useState(0); // total matching the search
 const [schoolAdds, setSchoolAdds] = useState(new Map()); // id → row, ticked here
 const [schoolRemoves, setSchoolRemoves] = useState(new Map()); // id → row, unticked here
 // Header checkbox over every page: { mode: "add" | "remove", search, delta }
 // where delta is how many schools it adds to / removes from the selection.
 const [schoolBulk, setSchoolBulk] = useState(null);
 const [schoolClearAll, setSchoolClearAll] = useState(false); // drop every saved school first
 const [inCycleMatchCount, setInCycleMatchCount] = useState(0); // saved schools matching the search
 const [hiddenSchools, setHiddenSchools] = useState(new Map()); // id → row, removed from the list with ✕
 const [hideAllSchools, setHideAllSchools] = useState(false); // "Remove All" empties the list
 const [stagedSchools, setStagedSchools] = useState([]); // uploaded via preview, NOT yet in DB
 const [schoolsLoading, setSchoolsLoading] = useState(false);
 // Bumped by openEdit so the school page is refetched even when nothing else
 // changed (e.g. after a save that just created uploaded schools).
 const [schoolsReloadKey, setSchoolsReloadKey] = useState(0);
 const [schoolSearch, setSchoolSearch] = useState("");
 const debouncedSchoolSearch = useDebounce(schoolSearch, 400);
 const [showSchoolUploadModal, setShowSchoolUploadModal] = useState(false);
 const [schoolUploadFile, setSchoolUploadFile] = useState(null);
 const [schoolUploadLoading, setSchoolUploadLoading] = useState(false);
 const [schoolUploadResult, setSchoolUploadResult] = useState(null);
 const schoolFileInputRef = useRef(null);
 const [schoolPage, setSchoolPage] = useState(1);

 // ── grade/competency form helpers ──
 const [selectedGrade, setSelectedGrade] = useState("");
 // Per-grade competency options fetched from Neo4j
 const [gradeCompetencyOptions, setGradeCompetencyOptions] = useState({}); // { gradeCode: [...] }
 const [gradeCompetencyLoading, setGradeCompetencyLoading] = useState({}); // { gradeCode: bool }
 const [gradeCompetencyErrors, setGradeCompetencyErrors] = useState({}); // { gradeCode: string }
 // Per-grade dropdown open state
 const [gradeDropdownOpen, setGradeDropdownOpen] = useState({}); // { gradeCode: bool }
 // Per-grade search filter
 const [gradeCompSearch, setGradeCompSearch] = useState({}); // { gradeCode: string }

 // Ref for outside-click detection on per-grade dropdowns
 const dropdownRefs = useRef({});

 // ── reminder state ──
 const [reminders, setReminders] = useState([]);
 const [pendingReminders, setPendingReminders] = useState([]);
 // A reminder is just "after N days from cycle start, completion must be at least X%",
 // so the form only holds those two numbers plus the channels. when_to_check /
 // what_to_check are constants filled in at payload time (see handleSaveReminder).
 const [reminderForm, setReminderForm] = useState({
  mode: [], when_value: "", threshold_value: "",
 });
 const [editingReminderId, setEditingReminderId] = useState(null);

 const EMPTY_REMINDER = { mode: [], when_value: "", threshold_value: "" };


 const reminderInputError = validateReminderInput(reminderForm);

 // Two reminders for the same day both fire (the engine dedupes per reminder id), so the
 // same checkpoint twice means every recipient is notified twice. Blocked here and in the
 // API (409). A matching threshold on a *different* day is only warned about: it is legal,
 // but because "after N days" fires on the first day at-or-after N where completion is
 // still short, two same-threshold reminders can land on the same run and repeat.
 const reminderConflict = (() => {
  const day = parseInt(reminderForm.when_value, 10);
  if (Number.isNaN(day)) return null;
  const existing = (editingId ? reminders : pendingReminders).filter(
   (r) => r.id !== editingReminderId
  );
  const threshold = parseInt(reminderForm.threshold_value, 10);

  const sameDay = existing.find(
   (r) => reminderDayOffset(r, form.start_date, form.end_date) === day
  );
  if (sameDay) {
   return { blocking: true, message: `A reminder for day ${day} already exists` };
  }

  const sameThreshold = existing.find((r) => r.threshold_value === threshold);
  if (sameThreshold) {
   const otherDay = reminderDayOffset(sameThreshold, form.start_date, form.end_date);
   return {
    blocking: false,
    message: `Day ${otherDay} already reminds below ${threshold}% — this will repeat it`,
   };
  }
  return null;
 })();

 // ── student category state ──
 const [studentCategories, setStudentCategories] = useState([]);
 const [availableThresholds, setAvailableThresholds] = useState([]);
 const [catForm, setCatForm] = useState({ name: "", threshold: "", operator: "gte", description: "", color: "#22c55e", display_order: "" });
 const [editingCatId, setEditingCatId] = useState(null);
 const EMPTY_CAT = { name: "", threshold: "", operator: "gte", description: "", color: "#22c55e", display_order: "" };

 // ── competency → {category_name, weight} map for the selected state ──
 const [compCategoryMap, setCompCategoryMap] = useState({});

 // ── previous round students (shown during cycle creation for round > 1) ──
 const [prevRoundData, setPrevRoundData] = useState(null);
 const [prevRoundLoading, setPrevRoundLoading] = useState(false);
 // Track selected students per category: { "A": Set(["student_id_1",...]), "B": Set([...]) }
 const [prevRoundSelections, setPrevRoundSelections] = useState({});
 // Track skipped categories: { "C": true }
 const [skippedCategories, setSkippedCategories] = useState({});
 // Saved skipped student IDs (loaded when editing an existing cycle) - use ref to avoid stale closure
 const savedSkippedStudentIdsRef = useRef(null);
 const resetCategoryForm = () => ({ name: "", threshold: "", operator: "gte", description: "", color: "#22c55e", display_order: "" });
 const CAT_COLORS = [
  { label: "Green", value: "#22c55e" },
  { label: "Yellow", value: "#eab308" },
  { label: "Red", value: "#ef4444" },
  { label: "Blue", value: "#3b82f6" },
  { label: "Purple", value: "#a855f7" },
  { label: "Gray", value: "#9ca3af" },
 ];
 // Comparison operators for student-category thresholds
 const OP_OPTIONS = [
  { value: "gte", label: "≥ Greater or equal" },
  { value: "gt", label: "> Greater than" },
  { value: "lte", label: "≤ Less or equal" },
  { value: "lt", label: "< Less than" },
  { value: "eq", label: "= Equal" },
 ];

 // ── Forget every unsaved school change (open, create, state change) ──
 const resetSchoolChanges = useCallback(() => {
  setSchoolAdds(new Map());
  setSchoolRemoves(new Map());
  setSchoolBulk(null);
  setSchoolClearAll(false);
  setHiddenSchools(new Map());
  setHideAllSchools(false);
  setStagedSchools([]);
 }, []);

 // Load competency→category/weight map when the selected state changes.
 // Skipped when isLocked — DetailCompetenciesWeightage fetches its own copy
 // for the read-only view, so this would just be wasted duplicate work.
 useEffect(() => {
  if (!isLocked && view === "form" && form.state_id) {
   fetchCompetencyCategoryMap(form.state_id)
    .then((res) => setCompCategoryMap(res.data || {}))
    .catch(() => setCompCategoryMap({}));
  } else {
   setCompCategoryMap({});
  }
 }, [isLocked, view, form.state_id]);

 // Weightage of the competencies currently selected across all grades —
 // one flat row per competency (helps decide student-category thresholds).
 const selectedCompWeightage = useMemo(() => {
  const rows = [];
  (form.grades || []).forEach((g) => {
   (g.competencies || []).forEach((c) => {
    const code = c.competency_code || c.competency_id;
    const info = compCategoryMap[code];
    if (!info) return; // skip competencies with no category mapping
    rows.push({ code, name: c.competency_name || code, category_name: info.category_name, weight: info.weight });
   });
  });
  return rows;
 }, [form.grades, compCategoryMap]);
 // Categorisation is cumulative across rounds (see backend _get_cumulative_scores_with_breakdown),
 // so for round > 1 the sensible/enforced max must include the previous round's
 // total weight too — not just this round's own newly-selected competencies.
 const previousRoundWeight = prevRoundData?.previous_round_total_weight || 0;
 const currentRoundWeight = useMemo(
  () => selectedCompWeightage.reduce((sum, r) => sum + (r.weight || 0), 0),
  [selectedCompWeightage]
 );
 const maxPossibleScore = currentRoundWeight + previousRoundWeight;

 // ── Load one page of the state's non-adopted schools (quarterly form) ──
 // Skipped when isLocked — the read-only view lists schools via DetailCycleSchools.
 useEffect(() => {
  if (view !== "form" || cycleType !== "quarterly" || isLocked || !form.state_id) {
   setSchoolRows([]);
   setSchoolTotal(0);
   return;
  }
  let cancelled = false;
  setSchoolsLoading(true);
  fetchSchools({
   state_id: form.state_id, is_adopted: false, is_active: true,
   page: schoolPage, page_size: SCHOOL_PAGE_SIZE, search: debouncedSchoolSearch,
   in_cycle_id: editingId || undefined,
  })
   .then((res) => {
    if (cancelled) return;
    // Past the last server page the API returns its last page again; those
    // pages only hold uploaded schools, so show no server rows there.
    setSchoolRows(res.current_page === schoolPage ? (res.data || []) : []);
    setSchoolTotal(res.total_count || 0);
   })
   .catch((err) => {
    if (cancelled) return;
    console.error("Failed to load non-adopted schools", err);
    setSchoolRows([]);
    setSchoolTotal(0);
   })
   .finally(() => { if (!cancelled) setSchoolsLoading(false); });
  return () => { cancelled = true; };
 }, [view, cycleType, isLocked, form.state_id, editingId, schoolPage, debouncedSchoolSearch, schoolsReloadKey]);

 // How many saved schools match the search — lets the header checkbox and
 // the "selected" count stay exact without loading the cycle's school list.
 useEffect(() => {
  if (view !== "form" || cycleType !== "quarterly" || isLocked || !form.state_id || !editingId) {
   setInCycleMatchCount(0);
   return;
  }
  let cancelled = false;
  fetchSchools({
   state_id: form.state_id, is_adopted: false, is_active: true,
   page: 1, page_size: 1, search: debouncedSchoolSearch, in_cycle_id: editingId, in_cycle_only: true,
  })
   .then((res) => { if (!cancelled) setInCycleMatchCount(res.total_count || 0); })
   .catch(() => { if (!cancelled) setInCycleMatchCount(0); });
  return () => { cancelled = true; };
 }, [view, cycleType, isLocked, form.state_id, editingId, debouncedSchoolSearch, schoolsReloadKey]);

 // Back to page 1 whenever the search or state changes
 useEffect(() => {
  setSchoolPage(1);
 }, [debouncedSchoolSearch, form.state_id]);

 // A different state makes every earlier pick invalid: the saved schools
 // belong to the old state, so they are cleared on save (clear_schools).
 const loadedStateIdRef = useRef(null);
 useEffect(() => {
  if (cycleType !== "quarterly" || !form.state_id) return;
  if (loadedStateIdRef.current === null) { loadedStateIdRef.current = String(form.state_id); return; }
  if (loadedStateIdRef.current === String(form.state_id)) return;
  loadedStateIdRef.current = String(form.state_id);
  resetSchoolChanges();
  if (editingId) setSchoolClearAll(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [form.state_id]);

 // ── Selection helpers ──
 // Server-side search is ILIKE '%term%' over these fields; mirror it so ticks
 // set by the header checkbox show on any page, and uploaded rows filter alike.
 const rowMatchesSearch = (row, term) => {
  const t = (term || "").trim().toLowerCase();
  if (!t) return true;
  return [row.name, row.udise_code, row.block_name, row.district_name, row.state_name]
   .some((v) => (v || "").toLowerCase().includes(t));
 };
 // Selected before this session's single ticks: saved in the cycle, unless
 // cleared, or covered by the header checkbox.
 const baseSelected = (row) => {
  if (schoolBulk && rowMatchesSearch(row, schoolBulk.search)) return schoolBulk.mode === "add";
  return !schoolClearAll && !!row.in_cycle;
 };
 const isSchoolSelected = (row) =>
  !schoolRemoves.has(row.id) && (schoolAdds.has(row.id) || baseSelected(row));
 const setSchoolsSelected = (rows, selected) => {
  const adds = new Map(schoolAdds);
  const removes = new Map(schoolRemoves);
  rows.forEach((row) => {
   const base = baseSelected(row);
   adds.delete(row.id);
   removes.delete(row.id);
   if (selected && !base) adds.set(row.id, row);
   if (!selected && base) removes.set(row.id, row);
  });
  setSchoolAdds(adds);
  setSchoolRemoves(removes);
 };
 const savedSchoolCount = editingId ? (detailCycle?.school_count || 0) : 0;
 const selectedNewCount = stagedSchools.filter((s) => s.is_new && s.selected !== false).length;
 const bulkDelta = !schoolBulk ? 0 : schoolBulk.mode === "add" ? schoolBulk.delta : -schoolBulk.delta;
 const selectedSchoolCount = Math.max(0,
  (schoolClearAll ? 0 : savedSchoolCount) + bulkDelta
  + schoolAdds.size - schoolRemoves.size
  + selectedNewCount);

 // Header checkbox: is every school matching the current search selected?
 const savedMatching = schoolClearAll ? 0 : inCycleMatchCount;
 const allMatchingSelected = (() => {
  if (hideAllSchools) return false;
  const search = debouncedSchoolSearch;
  const matchingRemoves = [...schoolRemoves.values()].some((r) => rowMatchesSearch(r, search));
  if (schoolBulk && schoolBulk.search === search) {
   return schoolBulk.mode === "add" && schoolTotal > 0 && !matchingRemoves;
  }
  if (schoolBulk) return false;
  const matchingAdds = [...schoolAdds.values()].filter((r) => rowMatchesSearch(r, search)).length;
  const matchingRemoveCount = [...schoolRemoves.values()].filter((r) => rowMatchesSearch(r, search)).length;
  return schoolTotal > 0 && savedMatching + matchingAdds - matchingRemoveCount >= schoolTotal;
 })();

 // Select / clear every school matching the search, on every page (the old
 // client-side list did this over everything it had loaded).
 const toggleAllMatching = () => {
  const search = debouncedSchoolSearch;
  const selecting = !allMatchingSelected;
  if (schoolBulk && schoolBulk.search !== search) {
   setToast({ type: "error", message: "Save the cycle first, then select or clear schools for another search." });
   return;
  }
  // Single ticks inside the search are replaced by the bulk choice
  setSchoolAdds((prev) => new Map([...prev].filter(([, r]) => !rowMatchesSearch(r, search))));
  setSchoolRemoves((prev) => new Map([...prev].filter(([, r]) => !rowMatchesSearch(r, search))));
  setSchoolBulk(selecting
   ? { mode: "add", search, delta: Math.max(0, schoolTotal - savedMatching) }
   : { mode: "remove", search, delta: savedMatching });
  // Uploaded rows in the search follow the header checkbox too
  setStagedSchools((prev) => prev.map((st) => (st.is_new && rowMatchesSearch(st, search) ? { ...st, selected: selecting } : st)));
 };

 // ── Load previous round students when creating a round > 1 cycle ──
 // Skipped when isLocked (published/completed) — that state renders the
 // read-only summary instead of the Skip Configuration section this feeds,
 // so fetching it would just be a wasted multi-second round trip. Also
 // continuous-only: Quarterly rounds are scored independently (no carried
 // weight, no Skip Configuration), so this data has no consumer there —
 // fetching it would just be a wasted round trip and a spurious "Previous
 // round weight" line in the weightage table below.
 useEffect(() => {
  if (!isLocked && view === "form" && cycleType === "continuous" && form.state_id && form.round_number && parseInt(form.round_number, 10) > 1) {
   const prevRound = parseInt(form.round_number, 10) - 1;
   setPrevRoundLoading(true);
   fetchPreviousRoundStudents({ state_id: form.state_id, previous_round: prevRound, cycle_type: cycleType })
    .then((res) => {
     const data = res.data || null;
     setPrevRoundData(data);
     // Start with empty sets — no students skipped by default (all included).
     // User can check individual students in Category C to mark them as skipped.
     if (data && data.categories) {
      const selections = {};
      data.categories.forEach((cat) => {
       selections[cat.category_name] = new Set();
      });
      setPrevRoundSelections(selections);
     }
     setSkippedCategories({});
    })
    .catch(() => { setPrevRoundData(null); setPrevRoundSelections({}); setSkippedCategories({}); })
    .finally(() => setPrevRoundLoading(false));
  } else {
   setPrevRoundData(null);
   setPrevRoundSelections({});
   setSkippedCategories({});
   setPrevRoundLoading(false);
  }
 }, [isLocked, view, form.state_id, form.round_number, cycleType]);

 // ── Restore skipped categories when editing and prevRoundData loads ──
 useEffect(() => {
  if (!prevRoundData || !prevRoundData.categories) return;
  if (!savedSkippedStudentIdsRef.current || savedSkippedStudentIdsRef.current.length === 0) return;

  const skippedSet = new Set(savedSkippedStudentIdsRef.current);
  const restoredSkipped = {};
  prevRoundData.categories.forEach((cat) => {
   if (cat.students && cat.students.length > 0) {
    const allSkipped = cat.students.every((s) => skippedSet.has(s.student_id));
    if (allSkipped) {
     restoredSkipped[cat.category_name] = true;
    }
   }
  });
  setSkippedCategories(restoredSkipped);
  savedSkippedStudentIdsRef.current = null;
 }, [prevRoundData]);

 // ── Previous round selection helpers ──
 const toggleStudentSelection = (catName, studentId) => {
  setPrevRoundSelections((prev) => {
   const catSet = new Set(prev[catName] || []);
   if (catSet.has(studentId)) catSet.delete(studentId); else catSet.add(studentId);
   return { ...prev, [catName]: catSet };
  });
 };
 const toggleSelectAll = (catName, students) => {
  setPrevRoundSelections((prev) => {
   const catSet = new Set(prev[catName] || []);
   const allIds = students.map((s) => s.student_id);
   const allSelected = allIds.length > 0 && allIds.every((id) => catSet.has(id));
   if (allSelected) {
    allIds.forEach((id) => catSet.delete(id));
   } else {
    allIds.forEach((id) => catSet.add(id));
   }
   return { ...prev, [catName]: catSet };
  });
 };
 const toggleSkipCategory = (catName) => {
  setSkippedCategories((prev) => ({ ...prev, [catName]: !prev[catName] }));
 };
 // Build is_skipped data for each student across all categories
 const getPrevRoundStudentsWithSkipped = () => {
  if (!prevRoundData || !prevRoundData.categories) return [];
  const result = [];
  for (const cat of prevRoundData.categories) {
   const selected = prevRoundSelections[cat.category_name] || new Set();
   const isSkipped = !!skippedCategories[cat.category_name];
   for (const s of cat.students) {
    result.push({
     ...s,
     category_name: cat.category_name,
     is_skipped: isSkipped ? true : selected.has(s.student_id),
    });
   }
  }
  return result;
 };

 // ── Fetch competencies for a grade from Neo4j ──
 const loadCompetenciesForGrade = useCallback(async (gradeCode, stateId) => {
  if (!stateId) {
   setGradeCompetencyErrors((prev) => ({
    ...prev,
    [gradeCode]: "State must be selected first",
   }));
   return;
  }
  setGradeCompetencyLoading((prev) => ({ ...prev, [gradeCode]: true }));
  setGradeCompetencyErrors((prev) => ({ ...prev, [gradeCode]: null }));
  try {
   const data = await fetchCompetenciesByGrade(gradeCode, stateId);
   setGradeCompetencyOptions((prev) => ({ ...prev, [gradeCode]: data || [] }));
  } catch (err) {
   console.error(`Failed to load competencies for ${gradeCode}`, err);
   setGradeCompetencyErrors((prev) => ({
    ...prev,
    [gradeCode]: "Failed to load competencies",
   }));
   setGradeCompetencyOptions((prev) => ({ ...prev, [gradeCode]: [] }));
  } finally {
   setGradeCompetencyLoading((prev) => ({ ...prev, [gradeCode]: false }));
  }
 }, []);

 // ── Load dropdowns ──
 useEffect(() => {
  const loadDropdowns = async () => {
   try {
    const [statesRes, yearsRes] = await Promise.all([
     fetchMasterData("states", { page_size: 100 }),
     fetchAssessmentYears(),
    ]);
    setStates(statesRes.data || []);
    setAssessmentYears(yearsRes.data || []);
   } catch (err) {
    console.error("Failed to load dropdowns", err);
   }
  };
  loadDropdowns();
  fetchAllSetsLite()
   .then((data) => setSetsLite(Array.isArray(data) ? data : []))
   .catch(() => setSetsLite([]));
 }, []);

 // ── Reactively fetch competencies for any grade that lacks loaded options ──
 // Skipped when isLocked — the read-only view never shows the "add
 // competency" grade picker these options feed.
 useEffect(() => {
  if (isLocked || !form.state_id) return;
  for (const g of form.grades) {
   const alreadyLoaded = gradeCompetencyOptions[g.grade_code] !== undefined;
   const currentlyLoading = gradeCompetencyLoading[g.grade_code];
   if (!alreadyLoaded && !currentlyLoading) {
    loadCompetenciesForGrade(g.grade_code, form.state_id);
   }
  }
 }, [isLocked, form.state_id, form.grades, gradeCompetencyOptions, gradeCompetencyLoading, loadCompetenciesForGrade]);

 // ── Load holiday calendar options for the selected state ──
 // Only the selected assessment year's calendar is offered: a cycle must use its own
 // year's holidays. Skipped when isLocked — the read-only view shows holiday_calendar_name
 // and never renders these options.
 useEffect(() => {
  if (!form.state_id || !form.assessment_year_id) {
   setHolidayCalendarOptions([]);
   if (!isLocked && form.holiday_calendar_id) setForm((prev) => ({ ...prev, holiday_calendar_id: "" }));
   return;
  }
  if (isLocked) return;
  fetchHolidays({ state_id: form.state_id, page_size: 200 })
   .then((res) => {
    const yearIds = new Set((res.data || []).map((h) => h.assessment_year_id));
    const opts = assessmentYears.filter(
     (y) => yearIds.has(y.id) && String(y.id) === String(form.assessment_year_id)
    );
    setHolidayCalendarOptions(opts);
    if (form.holiday_calendar_id && !opts.some((y) => String(y.id) === String(form.holiday_calendar_id))) {
     setForm((prev) => ({ ...prev, holiday_calendar_id: "" }));
    }
   })
   .catch(() => setHolidayCalendarOptions([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [form.state_id, form.assessment_year_id, assessmentYears, isLocked]);

 // ── Close dropdown on outside click ──
 useEffect(() => {
  const handleClickOutside = (e) => {
   const openGrades = Object.entries(gradeDropdownOpen).filter(([, open]) => open);
   if (openGrades.length === 0) return;
   const updates = {};
   for (const [gradeCode] of openGrades) {
    const el = dropdownRefs.current[gradeCode];
    if (el && !el.contains(e.target)) {
     updates[gradeCode] = false;
    }
   }
   if (Object.keys(updates).length > 0) {
    setGradeDropdownOpen((prev) => ({ ...prev, ...updates }));
   }
  };
  document.addEventListener("mousedown", handleClickOutside);
  return () => document.removeEventListener("mousedown", handleClickOutside);
 }, [gradeDropdownOpen]);

 // ── Close dropdown on ESC key ──
 useEffect(() => {
  const handleEsc = (e) => {
   if (e.key === "Escape") {
    const hasOpen = Object.values(gradeDropdownOpen).some(Boolean);
    if (hasOpen) setGradeDropdownOpen({});
   }
  };
  document.addEventListener("keydown", handleEsc);
  return () => document.removeEventListener("keydown", handleEsc);
 }, [gradeDropdownOpen]);

 // ── Auto-select current assessment year & holiday calendar ──
 const autoSelectCurrentYear = useCallback(() => {
  if (assessmentYears.length === 0) return {};
  const today = new Date().toISOString().slice(0, 10);
  const current = assessmentYears.find(
   (y) => y.start_date <= today && y.end_date >= today
  );
  return current
   ? { assessment_year_id: current.id }
   : {};
 }, [assessmentYears]);

 // ── Re-apply auto-select once assessment years finish loading ──
 // openCreate() runs on mount before fetchAssessmentYears() resolves, so its
 // one-shot autoSelectCurrentYear() call sees an empty list and picks
 // nothing. Once the list arrives, fill the year in if we're still on a
 // blank create form (never overwrite a value the user already set/loaded).
 useEffect(() => {
  if (editingId) return;
  if (view !== "form") return;
  if (form.assessment_year_id) return;
  const autoYear = autoSelectCurrentYear();
  if (autoYear.assessment_year_id) {
   setForm((prev) => (prev.assessment_year_id ? prev : { ...prev, ...autoYear }));
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [assessmentYears, editingId, view]);

 // ── Selected assessment year object (for date validation) ──
 const selectedAssessmentYear = useMemo(() => {
  if (!form.assessment_year_id) return null;
  return assessmentYears.find((y) => String(y.id) === String(form.assessment_year_id)) || null;
 }, [form.assessment_year_id, assessmentYears]);

 // ── Validate form (inline errors + date rules) ──
 // A dependent MCQ set unlocks from the Grid score earned in the same visit, so the
 // Grid set's competency must be in the same round and grade. {grade_code: [message]}
 // Grades left as saved only warn, so a cycle with legacy problems still saves other edits.
 const roundDependencyIssues = useMemo(() => {
  const byId = new Map(setsLite.map((s) => [s.id, s]));
  const issues = {};
  const warnings = {};
  for (const g of form.grades || []) {
   const codes = new Set(g.competencies.map((c) => c.competency_code));
   const unchanged = String(form.state_id) === savedStateId && savedGradeCodes[g.grade_code] === [...codes].sort().join("|");
   for (const s of setsLite) {
    if (!codes.has(s.competency_code) || String(s.competency_state_id) !== String(form.state_id)) continue;
    if ((s.set_type || "mcq") !== "mcq" || !s.dependent_set_id) continue;
    const target = byId.get(s.dependent_set_id);
    if (!target || codes.has(target.competency_code)) continue;
    ((unchanged ? warnings : issues)[g.grade_code] ||= []).push(
     `${s.competency_code} (set "${s.title}") depends on ${target.competency_code} (set "${target.title}"), but ${target.competency_code} isn't in this round. Add ${target.competency_code} to this grade or remove the link.`
    );
   }
  }
  return { issues, warnings };
 }, [form.grades, form.state_id, setsLite, savedGradeCodes, savedStateId]);

 const validateForm = useCallback(() => {
  const errors = {};

  if (!form.state_id) errors.state_id = "State is required";
  if (!form.round_number) errors.round_number = "Round is required";
  if (!form.title || !form.title.trim()) errors.title = "Title is required";
  else if (!/\p{L}/u.test(form.title.trim())) errors.title = "Title must contain at least one letter (not just numbers, spaces, or symbols)";
  if (!form.start_date) errors.start_date = "Start date is required";
  if (!form.end_date) errors.end_date = "End date is required";

  // Date: start < end
  if (form.start_date && form.end_date && form.start_date >= form.end_date) {
   errors.end_date = "End date must be after start date";
  }

  // Dates must fall inside assessment year range
  if (selectedAssessmentYear) {
   const ayStart = selectedAssessmentYear.start_date;
   const ayEnd = selectedAssessmentYear.end_date;
   if (form.start_date && form.start_date < ayStart) {
    errors.start_date = `Start date cannot be before assessment year start (${ayStart})`;
   }
   if (form.start_date && form.start_date > ayEnd) {
    errors.start_date = `Start date cannot be after assessment year end (${ayEnd})`;
   }
   if (form.end_date && form.end_date < ayStart) {
    errors.end_date = `End date cannot be before assessment year start (${ayStart})`;
   }
   if (form.end_date && form.end_date > ayEnd) {
    errors.end_date = `End date cannot be after assessment year end (${ayEnd})`;
   }
  }

  // At least one grade (with at least 1 competency) must be selected
  if (form.grades.length === 0) {
   errors.grades = "At least one grade with a competency must be added";
  } else {
   for (const g of form.grades) {
    if (g.competencies.length === 0) {
     errors.grades = `Each grade must have at least 1 competency (${g.grade_code} has none)`;
     break;
    }
    const missingOrder = g.competencies.find((c) => !(parseInt(c.competency_order, 10) >= 1));
    if (missingOrder) {
     errors.grades = `Set an order (starting at 1) for every competency (${g.grade_code} — ${missingOrder.competency_code} has none or an invalid order)`;
     break;
    }
    // Orders must be a consecutive sequence 1..N (no duplicates, no gaps)
    const orders = g.competencies.map((c) => parseInt(c.competency_order, 10)).sort((a, b) => a - b);
    const n = orders.length;
    const duplicate = orders.find((o, idx) => idx > 0 && o === orders[idx - 1]);
    if (duplicate != null) {
     errors.grades = `Competency order ${duplicate} is used more than once in ${g.grade_code} — each order number must be unique`;
     break;
    }
    const isConsecutive = orders.every((o, idx) => o === idx + 1);
    if (!isConsecutive) {
     errors.grades = `Competency orders in ${g.grade_code} must run consecutively from 1 to ${n} with no gaps`;
     break;
    }
   }
  }

  if (!errors.grades) {
   const [firstGrade, msgs] = Object.entries(roundDependencyIssues.issues)[0] || [];
   if (firstGrade) errors.grades = `${firstGrade}: ${msgs[0]}`;
  }

  // Quarterly cycles require at least one (existing or newly uploaded) school
  if (cycleType === "quarterly" && selectedSchoolCount === 0) {
   errors.school_ids = "At least one school must be selected";
  }

  return errors;
 }, [form, selectedAssessmentYear, cycleType, selectedSchoolCount, roundDependencyIssues]);

 // Re-validate whenever form changes
 useEffect(() => {
  setFormErrors(validateForm());
 }, [validateForm]);

 const hasErrors = Object.keys(formErrors).length > 0;

 // ── Load cycles ──
 const loadCycles = useCallback(async () => {
  setLoading(true);
  try {
   const params = { page, page_size: pageSize, cycle_type: cycleType };
   if (filterState) params.state_id = filterState;
   if (filterYear) params.assessment_year_id = filterYear;
   if (filterStatus) params.status = filterStatus;
   if (debouncedListSearch) params.search = debouncedListSearch;
   const res = await fetchCycles(params);
   setCycles(res.data || []);
   setTotalPages(res.total_pages || 1);
   setTotalCount(res.total_count || 0);
  } catch (err) {
   console.error("Failed to load cycles", err);
  } finally {
   setLoading(false);
  }
 }, [page, pageSize, filterState, filterYear, filterStatus, debouncedListSearch, cycleType]);

 // A new search term restarts paging; otherwise page 3 of the old term would
 // render against the new, shorter result set. Reset the page *instead of*
 // loading, so a search on page > 1 fires one request, not two.
 const lastListSearchRef = useRef(debouncedListSearch);
 // A save made while the form stays open marks the list stale; it reloads
 // when the user returns to it instead of while it is hidden.
 const listStaleRef = useRef(false);
 useEffect(() => {
  if (lastListSearchRef.current !== debouncedListSearch) {
   lastListSearchRef.current = debouncedListSearch;
   if (page !== 1) { setPage(1); return; }
  }
  listStaleRef.current = false;
  loadCycles();
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [loadCycles]);

 useEffect(() => {
  if (view === "list" && listStaleRef.current) {
   listStaleRef.current = false;
   loadCycles();
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [view]);

 const handlePageSizeChange = (newSize) => {
  setPageSize(newSize);
  setPage(1);
 };

 // ── Open create form ──
 // Set by a direct click (openCreate/openEdit) so the URL-restore effect
 // below doesn't run the same open a second time once the URL updates.
 const hasRestoredFromUrl = useRef(false);

 const openCreate = () => {
  hasRestoredFromUrl.current = true;
  setEditingId(null);
  const autoYear = autoSelectCurrentYear();
  setForm({ ...EMPTY_FORM, ...autoYear });
  setSavedGradeCodes({});
  setSavedStateId(null);
  setStagedSchools([]);
  setSelectedGrade("");
  setGradeCompetencyOptions({});
  setGradeCompetencyLoading({});
  setGradeCompetencyErrors({});
  setGradeDropdownOpen({});
  setGradeCompSearch({});
  setFormErrors({});
  setReminders([]);
  setPendingReminders([]);
  setReminderForm({ ...EMPTY_REMINDER });
  setStudentCategories([]);
  setAvailableThresholds([]);
  setCatForm({ ...EMPTY_CAT });
  setEditingCatId(null);
  setSchoolSearch("");
  resetSchoolChanges();
  loadedStateIdRef.current = null;
  savedSkippedStudentIdsRef.current = null;
  setView("form");
 };

 // ── Restore form/detail view from URL on refresh ──
 useEffect(() => {
  if (hasRestoredFromUrl.current) return;
  if (view === "form" && urlEditId) {
   hasRestoredFromUrl.current = true;
   openEdit(urlEditId);
  } else if (view === "form" && !urlEditId) {
   hasRestoredFromUrl.current = true;
   // Trigger create form setup
   openCreate();
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [view, urlEditId]);

 // ── Open edit form ──
 const openEdit = async (id) => {
  hasRestoredFromUrl.current = true;
  setFormLoading(true);
  try {
   const res = await fetchCycle(id);
   const c = res.data;
   setEditingId(id);
   setDetailCycle(c);
   const grades = (c.grades || []).map((g) => ({
    grade_code: g.grade_code,
    competencies: (g.competencies || []).map((comp) => ({
     competency_id: comp.competency_id,
     competency_code: comp.competency_code || comp.competency_id,
     competency_name: comp.competency_name || "",
     competency_order: comp.competency_order != null ? String(comp.competency_order) : "",
    })),
   }));

   // Quarterly schools are not loaded here: the table pages them from the
   // server (in_cycle tags saved members) and saving sends only the changes.
   loadedStateIdRef.current = c.state_id != null ? String(c.state_id) : null;
   setSavedStateId(c.state_id != null ? String(c.state_id) : null);
   setSavedGradeCodes(Object.fromEntries(grades.map((g) => [g.grade_code, g.competencies.map((x) => x.competency_code).sort().join("|")])));

   setForm({
    state_id: c.state_id,
    round_number: c.round_number || "",
    title: c.title || "",
    introduction: c.introduction || "",
    pm_po_notes: c.pm_po_notes || "",
    private_notes: c.private_notes || "",
    start_date: c.start_date,
    end_date: c.end_date,
    holiday_calendar_id: c.holiday_calendar_id || "",
    assessment_year_id: c.assessment_year_id || "",
    grades,
    school_ids: [],
   });
   setSelectedGrade("");
   setGradeCompetencyOptions({});
   setGradeCompetencyLoading({});
   setGradeCompetencyErrors({});
   setGradeDropdownOpen({});
   setGradeCompSearch({});
   setFormErrors({});
   setSchoolSearch("");
    resetSchoolChanges();
   // A published/completed cycle renders read-only (see isLocked below) via
   // its own independent Detail* components, each fetching their own copy —
   // so the editable wizard's data (grade competency options, reminders,
   // student categories) would just be fetched and thrown away unused.
   const locked = c.is_published || c.is_completed;
   if (!locked) {
    // Auto-fetch competency options for each existing grade
    for (const g of grades) {
     loadCompetenciesForGrade(g.grade_code, c.state_id);
    }
    loadReminders(id);
    loadStudentCategories(id);
    if (c.cycle_type === "quarterly") setSchoolsReloadKey((k) => k + 1);
   }
   // Save skipped student IDs for restoring after prevRoundData loads
   savedSkippedStudentIdsRef.current = c.skipped_student_ids || [];
   setReminderForm({ ...EMPTY_REMINDER });
   setCatForm({ ...EMPTY_CAT });
   setEditingCatId(null);
   setView("form", { edit: id });
  } catch (err) {
   setToast({ type: "error", message: "Failed to load cycle details" });
  } finally {
   setFormLoading(false);
  }
 };

 // ── Save cycle ──
 const handleSave = async () => {
  const errors = validateForm();
  setFormErrors(errors);
  if (Object.keys(errors).length > 0) return;

  setSaving(true);
  try {
   // Quarterly: send only what changed. New uploaded schools are created first
   // (only those still ticked) and their ids go in with the other additions.
   let addSchoolIds = [...schoolAdds.keys()];
   if (cycleType === "quarterly") {
    const newSchools = stagedSchools.filter((s) => s.is_new && s.selected !== false);
    if (newSchools.length > 0) {
     const batchRes = await createNonAdoptedBatch(
      newSchools.map((s) => ({
       name: s.name,
       block_id: s.block_id,
       udise_code: s.udise_code || null,
       latitude: s.latitude || null,
       longitude: s.longitude || null,
      })),
      form.state_id
     );
     if (batchRes.success && batchRes.school_ids) {
      addSchoolIds.push(...batchRes.school_ids);
     }
    }
    addSchoolIds = [...new Set(addSchoolIds)];
   }

   const payload = {
    cycle_type: cycleType,
    state_id: parseInt(form.state_id, 10),
    round_number: parseInt(form.round_number, 10),
    title: form.title.trim(),
    introduction: form.introduction || null,
    pm_po_notes: form.pm_po_notes || null,
    private_notes: form.private_notes || null,
    start_date: form.start_date,
    end_date: form.end_date,
    holiday_calendar_id: form.holiday_calendar_id || null,
    assessment_year_id: form.assessment_year_id || null,
    grades: form.grades.map((g) => ({
     grade_code: g.grade_code,
     competencies: g.competencies.map((c) => ({
      competency_code: c.competency_code,
      competency_name: c.competency_name || "",
      competency_order: c.competency_order ? parseInt(c.competency_order, 10) : null,
     })),
    })),
   };
   if (cycleType === "quarterly") {
    if (schoolClearAll) payload.clear_schools = true;
    if (schoolBulk?.mode === "add") payload.add_all_matching = { search: schoolBulk.search };
    if (schoolBulk?.mode === "remove") payload.remove_all_matching = { search: schoolBulk.search };
    if (addSchoolIds.length > 0) payload.add_school_ids = addSchoolIds;
    if (schoolRemoves.size > 0) payload.remove_school_ids = [...schoolRemoves.keys()];
   }

   if (editingId) {
    // Include skipped student IDs when updating (continuous cycles, round > 1)
    if (cycleType === "continuous" && prevRoundData && prevRoundData.categories) {
     const skippedIds = [];
     for (const cat of prevRoundData.categories) {
      if (skippedCategories[cat.category_name]) {
       cat.students.forEach((s) => skippedIds.push(s.student_id));
      }
     }
     payload.skipped_student_ids = skippedIds;
    }
    await updateCycle(editingId, payload);
    setToast({ type: "success", message: "Cycle updated successfully" });
    setStagedSchools([]);
    listStaleRef.current = true;
    await openEdit(editingId);
    return;
   } else {
    // Include locally-managed student categories in create payload
    if (studentCategories.length > 0) {
     payload.student_categories = studentCategories.map((c) => ({
      name: c.name,
      threshold: c.threshold,
      description: c.description || null,
      color: c.color || "#22c55e",
      display_order: c.display_order || 0,
     }));
    }
    // Include skipped student IDs (continuous cycles, round > 1)
    if (cycleType === "continuous" && prevRoundData && prevRoundData.categories) {
     const skippedIds = [];
     for (const cat of prevRoundData.categories) {
      if (skippedCategories[cat.category_name]) {
       cat.students.forEach((s) => skippedIds.push(s.student_id));
      }
     }
     if (skippedIds.length > 0) {
      payload.skipped_student_ids = skippedIds;
     }
    }
    const createResult = await createCycle(payload);
    const newCycleId = createResult?.data?.id;
    // The cycle already exists by now, so a failing reminder must not abort — but it
    // must not be silent either: a rejected reminder (duplicate day, missing days)
    // used to vanish here, leaving the user with a cycle and no reminder.
    let reminderErrors = [];
    if (newCycleId && pendingReminders.length > 0) {
     const results = await Promise.all(
      pendingReminders.map(({ id: _tmp, ...r }) =>
       addCycleReminder(newCycleId, r)
        .then(() => null)
        .catch((e) => apiErrorMessage(e, "Reminder could not be saved"))
      )
     );
     reminderErrors = results.filter(Boolean);
    }
    setPendingReminders([]);
    setToast(
     reminderErrors.length
      ? { type: "error", message: `Cycle created, but ${reminderErrors.length} reminder(s) failed: ${reminderErrors[0]}` }
      : { type: "success", message: "Cycle created successfully" }
    );
   }
   setStagedSchools([]);
   setView("list");
   loadCycles();
  } catch (err) {
   setToast({ type: "error", message: apiErrorMessage(err, "Failed to save cycle") });
  } finally {
   setSaving(false);
  }
 };

 // ── Publish ──
 const handlePublish = async (id) => {
  try {
   await publishCycle(id);
   setShowPublishConfirm(null);
   setToast({ type: "success", message: "Cycle Published" });
   loadCycles();
   if (detailCycle?.id === id) {
    const res = await fetchCycle(id);
    setDetailCycle(res.data);
   }
  } catch (err) {
   setShowPublishConfirm(null);
   setToast({ type: "error", message: apiErrorMessage(err, "Failed to publish") });
  }
 };

 // ── Unpublish ──
 const handleUnpublish = async (id) => {
  try {
   await unpublishCycle(id);
   setShowUnpublishConfirm(null);
   setToast({ type: "success", message: "Cycle Unpublished" });
   loadCycles();
   if (detailCycle?.id === id) {
    const res = await fetchCycle(id);
    setDetailCycle(res.data);
    // The editable form now replaces the read-only view, but openEdit skipped
    // these loads while the cycle was locked, so fill them in now.
    loadReminders(id);
    loadStudentCategories(id);
   }
  } catch (err) {
   setShowUnpublishConfirm(null);
   setToast({ type: "error", message: apiErrorMessage(err, "Failed to unpublish") });
  }
 };

 // ── Complete ──
 const handleComplete = async (id) => {
  try {
   await completeCycle(id);
   setShowCompleteConfirm(null);
   setToast({ type: "success", message: "Cycle Completed" });
   loadCycles();
   if (detailCycle?.id === id) {
    const res = await fetchCycle(id);
    setDetailCycle(res.data);
   }
  } catch (err) {
   setShowCompleteConfirm(null);
   setToast({ type: "error", message: apiErrorMessage(err, "Failed to complete") });
  }
 };

 // ── Delete ──
 const handleDelete = async (id) => {
  try {
   await deleteCycle(id);
   setShowDeleteConfirm(null);
   setToast({ type: "success", message: "Cycle deleted" });
   loadCycles();
   // If deleted from the form view, go back to list
   if (view !== "list") {
    setDetailCycle(null);
    setView("list");
   }
  } catch (err) {
   setShowDeleteConfirm(null);
   setToast({ type: "error", message: apiErrorMessage(err, "Failed to delete") });
  }
 };

 // ── Reminder helpers ──
 const loadReminders = useCallback(async (cycleId) => {
  try {
   const res = await listCycleReminders(cycleId);
   setReminders(res.data || []);
  } catch { setReminders([]); }
 }, []);

 const handleSaveReminder = async () => {
  if (reminderInputError) {
   setToast({ type: "error", message: reminderInputError.message });
   return;
  }
  const payload = {
   // after_days is the only trigger the form offers, and completion_below is the only
   // condition the reminder engine implements — both are constants, not user choices.
   when_to_check: "after_days",
   what_to_check: "completion_below",
   mode: (reminderForm.mode || []).join(","),
   when_value: reminderForm.when_value ? parseInt(reminderForm.when_value, 10) : null,
   threshold_value: reminderForm.threshold_value ? parseInt(reminderForm.threshold_value, 10) : null,
  };

  // Create mode — store locally, post to API after cycle is saved
  if (!editingId) {
   if (editingReminderId) {
    setPendingReminders(prev =>
     prev.map(r => r.id === editingReminderId ? { ...payload, id: editingReminderId } : r)
    );
   } else {
    setPendingReminders(prev => [...prev, { ...payload, id: `pending_${Date.now()}` }]);
   }
   setReminderForm({ ...EMPTY_REMINDER });
   setEditingReminderId(null);
   return;
  }

  // Edit mode — call API immediately
  try {
   if (editingReminderId) {
    await updateCycleReminder(editingId, editingReminderId, payload);
    setToast({ type: "success", message: "Reminder updated" });
   } else {
    await addCycleReminder(editingId, payload);
    setToast({ type: "success", message: "Reminder added" });
   }
   setReminderForm({ ...EMPTY_REMINDER });
   setEditingReminderId(null);
   loadReminders(editingId);
  } catch (err) {
   setToast({ type: "error", message: apiErrorMessage(err, "Failed to save reminder") });
  }
 };

 const startEditReminder = (reminder) => {
  setEditingReminderId(reminder.id);
  // Legacy mid_cycle / before_end rows open as their equivalent day offset, so saving
  // converts them to after_days without moving when they fire.
  const day = reminderDayOffset(reminder, form.start_date, form.end_date);
  setReminderForm({
   mode: reminder.mode ? String(reminder.mode).split(",").map((s) => s.trim()).filter(Boolean) : [],
   when_value: day != null ? String(day) : "",
   threshold_value: reminder.threshold_value != null ? String(reminder.threshold_value) : "",
  });
 };

 const cancelEditReminder = () => {
  setEditingReminderId(null);
  setReminderForm({ ...EMPTY_REMINDER });
 };

 const handleDeleteReminder = async (reminderId) => {
  if (!editingId) {
   setPendingReminders(prev => prev.filter(r => r.id !== reminderId));
   return;
  }
  try {
   await deleteCycleReminder(editingId, reminderId);
   loadReminders(editingId);
   setToast({ type: "success", message: "Reminder removed" });
  } catch (err) {
   setToast({ type: "error", message: apiErrorMessage(err, "Failed to remove reminder") });
  }
 };

 // ── Student Category helpers ──
 const loadStudentCategories = useCallback(async (cycleId) => {
  try {
   const res = await listStudentCategories(cycleId);
   setStudentCategories(res.data || []);
   setAvailableThresholds(res.available_thresholds || []);
  } catch {
   setStudentCategories([]);
   setAvailableThresholds([]);
  }
 }, []);

 const handleSaveCategory = async () => {
  const payload = {
   name: catForm.name.trim(),
   threshold: parseFloat(catForm.threshold),
   operator: catForm.operator || "gte",
   description: catForm.description || null,
   color: catForm.color || "#22c55e",
   display_order: catForm.display_order ? parseInt(catForm.display_order, 10) : 0,
  };
  if (!payload.name || isNaN(payload.threshold) || payload.threshold < 0) {
   setToast({ type: "error", message: "Name and a valid threshold (integer ≥ 0) are required" });
   return;
  }
  // Threshold can't exceed the total weight of the selected competencies.
  if (maxPossibleScore > 0 && payload.threshold > maxPossibleScore) {
   setToast({ type: "error", message: `Threshold cannot exceed the total weight (${maxPossibleScore}) of the selected competencies.` });
   return;
  }

  if (!editingId) {
   // Create mode: manage categories locally
   if (editingCatId) {
    setStudentCategories((prev) => prev.map((c) => (c.id === editingCatId ? { ...c, ...payload } : c)));
   } else {
    setStudentCategories((prev) => [...prev, { ...payload, id: `local-${Date.now()}` }]);
   }
   setCatForm(resetCategoryForm());
   setEditingCatId(null);
   setToast({ type: "success", message: editingCatId ? "Category updated" : "Category added" });
   return;
  }

  try {
   if (editingCatId) {
    await updateStudentCategory(editingId, editingCatId, payload);
    setToast({ type: "success", message: "Category updated" });
   } else {
    await createStudentCategory(editingId, payload);
    setToast({ type: "success", message: "Category added" });
   }
   setCatForm(resetCategoryForm());
   setEditingCatId(null);
   loadStudentCategories(editingId);
  } catch (err) {
   setToast({ type: "error", message: apiErrorMessage(err, "Failed to save category") });
  }
 };

 const startEditCategory = (cat) => {
  setEditingCatId(cat.id);
  setCatForm({
   name: cat.name || "",
   threshold: cat.threshold != null ? String(cat.threshold) : "",
   operator: cat.operator || "gte",
   description: cat.description || "",
   color: cat.color || "#22c55e",
   display_order: cat.display_order != null ? String(cat.display_order) : "",
  });
 };

 const cancelEditCategory = () => {
  setEditingCatId(null);
  setCatForm(resetCategoryForm());
 };

 const handleDeleteCategory = async (catId) => {
  if (!editingId) {
   // Create mode: remove from local state
   setStudentCategories((prev) => prev.filter((c) => c.id !== catId));
   setToast({ type: "success", message: "Category removed" });
   return;
  }
  try {
   await deleteStudentCategory(editingId, catId);
   loadStudentCategories(editingId);
   setToast({ type: "success", message: "Category removed" });
  } catch (err) {
   setToast({ type: "error", message: apiErrorMessage(err, "Failed to remove category") });
  }
 };

 // ── Grade helpers ──
 const addGrade = () => {
  if (!selectedGrade || form.grades.some((g) => g.grade_code === selectedGrade)) return;
  setForm({ ...form, grades: [...form.grades, { grade_code: selectedGrade, competencies: [] }] });
  // Auto-fetch competencies for the newly added grade
  if (form.state_id) {
   loadCompetenciesForGrade(selectedGrade, form.state_id);
  }
  setSelectedGrade("");
 };
 const removeGrade = (gc) => {
  setForm({ ...form, grades: form.grades.filter((g) => g.grade_code !== gc) });
  // Clean up state for removed grade
  setGradeCompetencyOptions((prev) => { const n = { ...prev }; delete n[gc]; return n; });
  setGradeCompetencyLoading((prev) => { const n = { ...prev }; delete n[gc]; return n; });
  setGradeCompetencyErrors((prev) => { const n = { ...prev }; delete n[gc]; return n; });
  setGradeDropdownOpen((prev) => { const n = { ...prev }; delete n[gc]; return n; });
  setGradeCompSearch((prev) => { const n = { ...prev }; delete n[gc]; return n; });
  delete dropdownRefs.current[gc];
 };

 // ── Competency helpers (multi-select from fetched options) ──
 const toggleCompetencyForGrade = (gradeCode, comp) => {
  setForm({
   ...form,
   grades: form.grades.map((g) => {
    if (g.grade_code !== gradeCode) return g;
    const exists = g.competencies.some((c) => c.competency_code === comp.code);
    if (exists) {
     return { ...g, competencies: g.competencies.filter((c) => c.competency_code !== comp.code) };
    }
    return {
     ...g,
     competencies: [
      ...g.competencies,
      { competency_code: comp.code, competency_name: comp.name, competency_order: "" },
     ],
    };
   }),
  });
 };
 const removeCompetencyFromGrade = (gradeCode, compCode) => {
  setForm({
   ...form,
   grades: form.grades.map((g) => {
    if (g.grade_code !== gradeCode) return g;
    const removed = g.competencies.find((c) => c.competency_code === compCode);
    const removedOrder = parseInt(removed?.competency_order, 10);
    const remaining = g.competencies.filter((c) => c.competency_code !== compCode);
    if (!(removedOrder >= 1)) {
     return { ...g, competencies: remaining };
    }
    // Close the gap: shift down any already-numbered competency above the removed slot.
    // Competencies that don't have an order yet are left untouched (never auto-filled).
    return {
     ...g,
     competencies: remaining.map((c) => {
      const o = parseInt(c.competency_order, 10);
      return o >= 1 && o > removedOrder ? { ...c, competency_order: String(o - 1) } : c;
     }),
    };
   }),
  });
 };

 // Helper for state name
 const stateName = (id) => states.find((s) => String(s.id) === String(id))?.name || id;

 // inline error helper
 const FieldError = ({ field }) => {
  if (!formErrors[field]) return null;
  return <p className="text-xs text-red-500 mt-1">{formErrors[field]}</p>;
 };


 // Helper: students grouped by category for detail view
 const DetailStudentsByCategory = ({ cycleId }) => {
  const [data, setData] = useState(null);
  const [gradeFilter, setGradeFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const loadData = useCallback(async () => {
   setLoading(true);
   try {
    const params = {};
    if (gradeFilter) params.grade = parseInt(gradeFilter, 10);
    const res = await fetchStudentsByCategory(cycleId, params);
    setData(res.data || null);
   } catch { setData(null); }
   finally { setLoading(false); }
  }, [cycleId, gradeFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleExport = async () => {
   setExporting(true);
   try {
    const params = {};
    if (gradeFilter) params.grade = parseInt(gradeFilter, 10);
    const blob = await exportStudentsCategoryCsv(cycleId, params);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `students_by_category_${cycleId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
   } catch { /* ignore */ }
   finally { setExporting(false); }
  };

  if (!data && !loading) return null;

  return (
   <div className="mb-4">
    <div className="flex items-center justify-between mb-3">
     <div>
      <h3 className="text-sm font-semibold text-[#24292f]">Students by Category</h3>
      {data && data.rounds_included && data.rounds_included.length > 0 && (
       <p className="text-[11px] text-[#8b949e] mt-0.5">
        Cumulative scores from {data.rounds_included.length === 1 ? "Round" : "Rounds"}{" "}
        {data.rounds_included.join(", ")}
       </p>
      )}
      {data && (!data.rounds_included || data.rounds_included.length === 0) && (
       <p className="text-[11px] text-[#8b949e] mt-0.5">No scoring data available yet</p>
      )}
     </div>
     <div className="flex items-center gap-2">
      <select value={gradeFilter} onChange={(e) => setGradeFilter(e.target.value)}
       className="bg-white border border-[#d0d7de] rounded-lg px-2 py-1 text-xs text-[#24292f]">
       <option value="">All Grades</option>
       {[1,2,3,4,5,6,7,8,9,10,11,12].map((g) => (
        <option key={g} value={g}>Grade {g}</option>
       ))}
      </select>
      <button onClick={handleExport} disabled={exporting}
       className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-white border border-[#d0d7de] rounded-lg hover:bg-[#f3f4f6] disabled:opacity-40">
       <HiOutlineDownload className="text-sm" /> {exporting ? "Exporting…" : "CSV"}
      </button>
     </div>
    </div>

    {loading ? (
     <p className="text-xs text-[#8b949e] italic">Loading…</p>
    ) : data && data.categories && data.categories.length > 0 ? (
     <div className="space-y-4">
      {data.categories.map((group) => (
       <div key={group.category_name} className="border border-[#e5e7eb] rounded-lg overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-2 bg-[#f6f8fa]">
         <span className="text-xs font-semibold text-[#24292f]">
          {group.category_name} (Threshold {OP_SYMBOL[group.operator || "gte"]} {group.threshold}) — {group.student_count} student{group.student_count !== 1 ? "s" : ""}
         </span>
        </div>
        {group.students.length > 0 ? (
         <table className="w-full text-xs">
          <thead>
           <tr className="bg-white">
            <th className="text-left px-3 py-1.5 text-[#8b949e]">Name</th>
            <th className="text-left px-3 py-1.5 text-[#8b949e]">SRN</th>
            <th className="text-left px-3 py-1.5 text-[#8b949e]">Grade</th>
            <th className="text-left px-3 py-1.5 text-[#8b949e]">School</th>
            <th className="text-left px-3 py-1.5 text-[#8b949e]">Total Score</th>
            <th className="text-left px-3 py-1.5 text-[#8b949e]">Score %</th>
            <th className="text-left px-3 py-1.5 text-[#8b949e]">Rounds</th>
           </tr>
          </thead>
          <tbody>
           {group.students.map((s) => (
            <tr key={s.student_id} className="border-t border-[#f0f0f0]">
             <td className="px-3 py-1.5">{s.student_name}</td>
             <td className="px-3 py-1.5">{s.srn || "—"}</td>
             <td className="px-3 py-1.5">{s.grade || "—"}</td>
             <td className="px-3 py-1.5">{s.school_name || "—"}</td>
             <td className="px-3 py-1.5 font-medium">{s.total_weighted_score} / {s.max_possible_score}</td>
             <td className="px-3 py-1.5">{s.score_percentage}%</td>
             <td className="px-3 py-1.5">{s.rounds_assessed}</td>
            </tr>
           ))}
          </tbody>
         </table>
        ) : (
         <p className="text-xs text-[#8b949e] italic px-3 py-2">No students in this category</p>
        )}
       </div>
      ))}
      <p className="text-xs text-[#8b949e]">Total students: {data.total_students}{data.rounds_included && data.rounds_included.length > 0 ? ` (cumulative from round${data.rounds_included.length > 1 ? "s" : ""} ${data.rounds_included.join(", ")})` : ""}</p>
     </div>
    ) : (
     <p className="text-xs text-[#8b949e] italic">No categorised data available. Students are classified using cumulative scores across rounds. Ensure scores have been synced and competency categories are defined in the system.</p>
    )}
   </div>
  );
 };

 // ═══════════════════════════════════════════════════════════════════════
 // RENDER
 // ═══════════════════════════════════════════════════════════════════════

 // ── FORM VIEW (CREATE / EDIT / READ-ONLY when published or completed) ──
 if (view === "form") {
  // A full-height centered spinner, not a small boxed skeleton, so this
  // reads as the whole page loading rather than just one card/section.
  // prevRoundLoading (round > 1's previous-round weight) is NOT included
  // here — it's a separate network+Neo4j round trip that can take a couple
  // seconds even with little data, and blocking the entire form on it made
  // every round-2+ cycle feel slow to open. The Selected Competencies
  // Weightage section below shows its own inline "Loading…" row instead.
  if (formLoading) {
   return (
    <div className="min-h-[70vh] flex items-center justify-center">
     <div className="w-8 h-8 border-4 border-[#0078d4] border-t-transparent rounded-full animate-spin"></div>
    </div>
   );
  }
  // A published or completed cycle is read-only here (isLocked, computed
  // above) — its editable wizard fields are replaced with the same
  // read-only summary the old detail view rendered, driven off detailCycle
  // (fetched by openEdit above).
  const c = detailCycle;
  return (
   <div className="p-4 sm:p-6 max-w-7xl mx-auto">
    {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    <div className="bg-white rounded-xl border border-[#e5e7eb] w-full">
     <div className="sticky top-14 z-40 flex items-center justify-between gap-3 px-6 py-4 border-b border-[#e5e7eb] bg-white rounded-t-xl">
      <div className="flex items-center gap-3">
       <button
        onClick={() => setView("list")}
        className="w-8 h-8 flex items-center justify-center rounded-md text-[#57606a] hover:bg-[#f3f4f6] transition-all border-none bg-transparent cursor-pointer"
        title="Back"
       >
        <HiOutlineChevronLeft className="text-lg" />
       </button>
       <div>
        <h2 className="m-0 text-[15px] font-semibold text-[#24292f]">
         {isLocked
          ? (c.title || `${c.cycle_type === "quarterly" ? "Quarterly" : "Continuous"} Cycle`)
          : `${editingId ? "Edit" : "Create"} ${cycleType === "quarterly" ? "Quarterly" : "Continuous"} Cycle`}
        </h2>
       </div>
      </div>
      {canManageCycles && editingId && detailCycle && (
       <div className="flex gap-2">
        {!detailCycle.is_published && !detailCycle.is_completed && (
         <>
          <button onClick={() => setShowPublishConfirm(detailCycle)} className="px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700">Publish</button>
          <button onClick={() => setShowDeleteConfirm(detailCycle)} className="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700">Delete</button>
         </>
        )}
        {detailCycle.is_published && !detailCycle.is_completed && (
         <>
          <button onClick={() => setShowUnpublishConfirm(detailCycle)} className="px-4 py-2 text-sm font-medium bg-yellow-500 text-white rounded-lg hover:bg-yellow-600">Unpublish</button>
          <button onClick={() => setShowCompleteConfirm(detailCycle)} className="px-4 py-2 text-sm font-medium bg-orange-500 text-white rounded-lg hover:bg-orange-600">Complete</button>
         </>
        )}
       </div>
      )}
     </div>
     <div className="p-4 sm:p-6">

     {isLocked ? (
      <>
       {c.cycle_type === "continuous" && (
        <div className="mb-6 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg">
         <p className="text-xs text-blue-700"><strong>Note:</strong> Only adopted schools will be added in Continuous Cycle.</p>
        </div>
       )}

       {/* ── 1. Basic Info ── */}
       <fieldset className="mb-6">
        <legend className="text-sm font-semibold text-[#24292f] mb-3">Basic Info</legend>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
         <div>
          <label className="block text-xs font-medium text-[#24292f] mb-1">State</label>
          <div className="text-sm text-[#24292f] py-2">{stateName(c.state_id) || "—"}</div>
         </div>
         <div>
          <label className="block text-xs font-medium text-[#24292f] mb-1">Round</label>
          <div className="text-sm text-[#24292f] py-2">{c.round_number ? `Round ${c.round_number}` : "—"}</div>
         </div>
         <div className="md:col-span-2">
          <label className="block text-xs font-medium text-[#24292f] mb-1">Title</label>
          <div className="text-sm text-[#24292f] py-2">{c.title || "—"}</div>
         </div>
         <div className="md:col-span-2">
          <fieldset className="border border-[#e5e7eb] rounded-lg p-4 m-0">
           <legend className="text-xs font-semibold text-[#57606a] px-2">Introduction</legend>
           <TiptapContentStyles />
           <div className="tiptap-content text-sm text-[#24292f] prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: renderTiptapHtml(c.introduction) || "<p class='text-[#8b949e] italic'>No introduction</p>" }} />
          </fieldset>
         </div>
         <div className="md:col-span-2">
          <fieldset className="border border-[#e5e7eb] rounded-lg p-4 m-0">
           <legend className="text-xs font-semibold text-[#57606a] px-2">Notes</legend>
           <div className="grid grid-cols-1 gap-4">
            <div>
             <div className="text-xs text-gray-500 mb-1">PM/PO Notes</div>
             <div className="text-sm text-[#24292f] whitespace-pre-wrap">{c.pm_po_notes || "—"}</div>
            </div>
            <div>
             <div className="text-xs text-gray-500 mb-1">Private Notes</div>
             <div className="text-sm text-[#24292f] whitespace-pre-wrap">{c.private_notes || "—"}</div>
            </div>
           </div>
          </fieldset>
         </div>
        </div>
       </fieldset>

       {/* ── 2. Assessment Year & Holiday Calendar ── */}
       <fieldset className="mb-6">
        <legend className="text-sm font-semibold text-[#24292f] mb-3">Assessment Year &amp; Holiday Calendar</legend>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
         <div>
          <label className="block text-xs font-medium text-[#24292f] mb-1">Assessment Year</label>
          <div className="text-sm text-[#24292f] py-2">{c.assessment_year_name || "—"}</div>
         </div>
         <div>
          <label className="block text-xs font-medium text-[#24292f] mb-1">Holiday Calendar</label>
          <div className="text-sm text-[#24292f] py-2">{c.holiday_calendar_name || "—"}</div>
         </div>
        </div>
       </fieldset>

       {/* ── 3. Timeline ── */}
       <fieldset className="mb-6">
        <legend className="text-sm font-semibold text-[#24292f] mb-3">Timeline</legend>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
         <div>
          <label className="block text-xs font-medium text-[#24292f] mb-1">Start Date</label>
          <div className="text-sm text-[#24292f] py-2">{c.start_date}</div>
         </div>
         <div>
          <label className="block text-xs font-medium text-[#24292f] mb-1">End Date</label>
          <div className="text-sm text-[#24292f] py-2">{c.end_date}</div>
         </div>
         <div>
          <label className="block text-xs font-medium text-[#24292f] mb-1">Effective End Date</label>
          <div className="text-sm text-[#24292f] py-2">{c.effective_end_date || "—"}</div>
         </div>
         <div>
          <label className="block text-xs font-medium text-[#24292f] mb-1">Status</label>
          <div className="flex gap-2 py-2">
           <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${c.is_published ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
            {c.is_published ? "Published" : "Draft"}
           </span>
           {c.is_completed && <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-gray-100 text-gray-700">Completed</span>}
          </div>
         </div>
        </div>
       </fieldset>

       {/* ── 4. Grades & Competencies ── */}
       <fieldset className="mb-6">
        <legend className="text-sm font-semibold text-[#24292f] mb-3">Grades &amp; Competencies</legend>
        {c.grades && c.grades.length > 0 ? (
         c.grades.map((g) => (
          <div key={g.id} className="border border-[#e5e7eb] rounded-lg mb-4">
           <div className="flex items-center justify-between px-4 py-2.5 bg-[#f6f8fa] border-b border-[#e5e7eb]">
            <span className="text-sm font-semibold text-blue-700">{g.grade_code}</span>
            <span className="text-[11px] text-[#8b949e]">{g.competencies.length} selected</span>
           </div>
           <div className="p-4">
            {g.competencies.length > 0 ? (
             <div className="flex flex-wrap gap-2">
              {[...g.competencies]
               .sort((a, b) => (a.competency_order ?? Infinity) - (b.competency_order ?? Infinity))
               .map((comp) => (
               <span key={comp.competency_code || comp.id}
                className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-800 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-blue-200">
                <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-blue-600 text-white text-[10px] font-bold flex-shrink-0" title="Competency order">
                 {comp.competency_order ?? "—"}
                </span>
                <HiOutlineCheckCircle className="text-blue-500 flex-shrink-0" />
                <span className="font-semibold">{comp.competency_code || comp.competency_id}</span>
                {comp.competency_name && <span className="text-blue-600">— {comp.competency_name}</span>}
               </span>
              ))}
             </div>
            ) : (
             <p className="text-xs text-[#8b949e] italic">No competencies selected yet.</p>
            )}
           </div>
          </div>
         ))
        ) : (
         <p className="text-xs text-[#8b949e] italic">No grades added yet.</p>
        )}
       </fieldset>

       {/* Read-only reference: currently selected competencies and their total weight */}
       <DetailCompetenciesWeightage stateId={c.state_id} grades={c.grades} roundNumber={c.round_number} cycleType={c.cycle_type} />

       {/* ── 4b. Student Categories ── */}
       <fieldset className="mb-6">
        <legend className="text-sm font-semibold text-[#24292f] mb-3">Student Categories</legend>
        <DetailStudentCategories cycleId={c.id} inline />
       </fieldset>

       {/* ── 5. Schools (quarterly only) ── */}
       {c.cycle_type === "quarterly" && (
        <fieldset className="mb-6">
         <legend className="text-sm font-semibold text-[#24292f] mb-3">
          Schools{typeof c.school_count === "number" ? ` (${c.school_count.toLocaleString()})` : ""}
         </legend>
         <DetailCycleSchools cycleId={c.id} totalHint={c.school_count} />
        </fieldset>
       )}

       {/* ── 6. Reminders ── */}
       <fieldset className="mb-6">
        <legend className="text-sm font-semibold text-[#24292f] mb-3">Reminders</legend>
        <DetailReminders cycleId={c.id} inline startDate={c.start_date} endDate={c.effective_end_date || c.end_date} />
       </fieldset>
      </>
     ) : (
      <>
     {cycleType === "continuous" && (
      <div className="mb-6 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg">
       <p className="text-xs text-blue-700"><strong>Note:</strong> Only adopted schools will be added in Continuous Cycle.</p>
      </div>
     )}

     {/* ── 1. Basic Info ── */}
     <fieldset className="mb-6">
      <legend className="text-sm font-semibold text-[#24292f] mb-3">Basic Info</legend>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
       <div>
        <label className="block text-xs font-medium text-[#24292f] mb-1">State <span className="text-red-500">*</span></label>
        <SearchableDropdown
         value={String(form.state_id || "")}
         onChange={(id) => setForm({ ...form, state_id: id })}
         placeholder="Select state…"
         fetchFn={(search, page = 1) => fetchMasterData("states", { page, page_size: 20, search })}
         resolveFn={(id) => {
          // The page already holds the states list — resolve the selected
          // label from it; only hit the API if it isn't there.
          const found = states.find((st) => String(st.id) === String(id));
          if (found) return Promise.resolve(found);
          return fetchMasterData("states", { page: 1, page_size: 20, search: "" })
           .then((res) => (res.data || []).find((st) => String(st.id) === String(id)));
         }}
         cacheNamespace="cycle_state_id"
         variant="form"
        />
        <FieldError field="state_id" />
       </div>
       <div>
        <label className="block text-xs font-medium text-[#24292f] mb-1">Round <span className="text-red-500">*</span></label>
        <select value={form.round_number} onChange={(e) => setForm({ ...form, round_number: e.target.value })}
         className={selectClass}>
         <option value="">Select round…</option>
         {Array.from({ length: cycleType === "continuous" ? 8 : 4 }, (_, i) => i + 1).map((r) => (
          <option key={r} value={r}>Round {r}</option>
         ))}
        </select>
        <FieldError field="round_number" />
       </div>
       <div className="md:col-span-2">
        <label className="block text-xs font-medium text-[#24292f] mb-1">Title <span className="text-red-500">*</span></label>
        <input type="text" placeholder="Enter cycle title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
         className={inputClass} />
        <FieldError field="title" />
       </div>
       <div className="md:col-span-2">
        <fieldset className="border border-[#e5e7eb] rounded-lg p-4 m-0">
         <legend className="text-xs font-semibold text-[#57606a] px-2">Introduction</legend>
         <p className="text-[11px] text-[#8b949e] mb-2 mt-0">An introductory text shown to the student before the assessment begins.</p>
         <TiptapEditor
          value={form.introduction}
          onChange={(text) => setForm({ ...form, introduction: text })}
          placeholder="Enter introduction text for students..."
          minHeight="260px"
          enableColors
         />
        </fieldset>
       </div>
       <div className="md:col-span-2">
        <fieldset className="border border-[#e5e7eb] rounded-lg p-4 m-0">
         <legend className="text-xs font-semibold text-[#57606a] px-2">Notes</legend>
         <div className="grid grid-cols-1 gap-5">
          <div>
           <label className="block text-xs font-medium text-[#24292f] mb-1">PM/PO Notes</label>
           <textarea
            rows={3}
            placeholder="Notes visible to PM and PO…"
            value={form.pm_po_notes}
            onChange={(e) => setForm({ ...form, pm_po_notes: e.target.value })}
            className={inputClass}
           />
          </div>
          <div>
           <label className="block text-xs font-medium text-[#24292f] mb-1">Private Notes</label>
           <textarea
            rows={3}
            placeholder="Private notes…"
            value={form.private_notes}
            onChange={(e) => setForm({ ...form, private_notes: e.target.value })}
            className={inputClass}
           />
          </div>
         </div>
        </fieldset>
       </div>
      </div>
     </fieldset>

     {/* ── 2. Assessment Year & Holiday Calendar (ABOVE Timeline) ── */}
     <fieldset className="mb-6">
      <legend className="text-sm font-semibold text-[#24292f] mb-3">Assessment Year &amp; Holiday Calendar</legend>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
       <div>
        <label className="block text-xs font-medium text-[#24292f] mb-1">Assessment Year</label>
        <select value={form.assessment_year_id} onChange={(e) => setForm({ ...form, assessment_year_id: e.target.value })}
         className={selectClass}>
         <option value="">None</option>
         {assessmentYears.map((y) => <option key={y.id} value={y.id}>{y.name}</option>)}
        </select>
       </div>
       <div>
        <label className="block text-xs font-medium text-[#24292f] mb-1">Holiday Calendar</label>
        <select value={form.holiday_calendar_id} onChange={(e) => setForm({ ...form, holiday_calendar_id: e.target.value })}
         className={selectClass}>
         <option value="">None</option>
         {holidayCalendarOptions.map((y) => <option key={y.id} value={y.id}>{y.name}</option>)}
        </select>
       </div>
      </div>
     </fieldset>

     {/* ── 3. Timeline (BELOW Assessment Year) ── */}
     <fieldset className="mb-6">
      <legend className="text-sm font-semibold text-[#24292f] mb-3">Timeline</legend>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
       <div>
        <label className="block text-xs font-medium text-[#24292f] mb-1">Start Date <span className="text-red-500">*</span></label>
        <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })}
         ref={(el) => { if (el && !el._noScroll) { el._noScroll = true; el.addEventListener('wheel', (ev) => ev.preventDefault(), { passive: false }); } }}
         className={inputClass} />
        <FieldError field="start_date" />
       </div>
       <div>
        <label className="block text-xs font-medium text-[#24292f] mb-1">End Date <span className="text-red-500">*</span></label>
        <input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })}
         ref={(el) => { if (el && !el._noScroll) { el._noScroll = true; el.addEventListener('wheel', (ev) => ev.preventDefault(), { passive: false }); } }}
         className={inputClass} />
        <FieldError field="end_date" />
       </div>
      </div>
     </fieldset>

     {/* ── 4. Grades + Competencies ── */}
     <fieldset className="mb-6">
      <legend className="text-sm font-semibold text-[#24292f] mb-3">Grades &amp; Competencies</legend>

      {/* Add grade row */}
      <div className="flex items-end gap-3 mb-4">
       <div className="flex-1">
        <label className="block text-xs font-medium text-[#24292f] mb-1">Select Grade</label>
        <select value={selectedGrade} onChange={(e) => setSelectedGrade(e.target.value)}
         className={selectClass}>
         <option value="">Select grade…</option>
         {CONFIG_GRADES.filter((g) => !form.grades.some((fg) => fg.grade_code === g)).map((g) => (
          <option key={g} value={g}>{g}</option>
         ))}
        </select>
       </div>
       <button onClick={addGrade} type="button" disabled={!selectedGrade}
        className="px-4 py-2 bg-[#0078d4] text-white text-xs font-medium rounded-lg hover:bg-[#106ebe] disabled:opacity-40 whitespace-nowrap h-[38px]">
        <HiOutlinePlus className="inline -mt-0.5 mr-1" />Add Grade
       </button>
      </div>

      {/* Per-grade sections */}
      {form.grades.map((grade) => {
        const options = gradeCompetencyOptions[grade.grade_code] || [];
        const isLoading = gradeCompetencyLoading[grade.grade_code];
        const error = gradeCompetencyErrors[grade.grade_code];
        const isDropdownOpen = gradeDropdownOpen[grade.grade_code] || false;
        const searchTerm = gradeCompSearch[grade.grade_code] || "";
        const selectedIds = new Set(grade.competencies.map((c) => c.competency_code));
        const filteredOptions = options.filter((o) =>
         !searchTerm || `${o.code} ${o.name}`.toLowerCase().includes(searchTerm.toLowerCase())
        );
        return (
        <div key={grade.grade_code} className="border border-[#e5e7eb] rounded-lg mb-4 overflow-visible">
         {/* Grade header */}
         <div className="flex items-center justify-between px-4 py-2.5 bg-[#f6f8fa] border-b border-[#e5e7eb]">
          <span className="text-sm font-semibold text-blue-700">{grade.grade_code}</span>
          <div className="flex items-center gap-2">
           <span className="text-[11px] text-[#8b949e]">{grade.competencies.length} selected</span>
           <button onClick={() => removeGrade(grade.grade_code)} className="text-red-500 hover:text-red-700" title="Remove grade">
            <HiOutlineTrash className="text-sm" />
           </button>
          </div>
         </div>

         <div className="p-4">
          {(roundDependencyIssues.issues[grade.grade_code] || []).map((msg) => (
           <p key={msg} className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-2">{msg}</p>
          ))}
          {(roundDependencyIssues.warnings[grade.grade_code] || []).map((msg) => (
           <p key={msg} className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-2">{msg}</p>
          ))}
          {/* Multi-select dropdown */}
          <div className="relative mb-3" ref={(el) => { if (el) dropdownRefs.current[grade.grade_code] = el; }}>
           <label className="block text-[11px] text-[#8b949e] mb-1">Select Competencies</label>
           <button type="button"
            onClick={() => setGradeDropdownOpen((prev) => ({ ...prev, [grade.grade_code]: !isDropdownOpen }))}
            className={`${inputClass} text-left flex items-center justify-between`}>
            <span className="truncate">
             {isLoading ? "Loading competencies…"
              : error ? error
              : grade.competencies.length === 0 ? "Click to select competencies…"
              : `${grade.competencies.length} competenc${grade.competencies.length === 1 ? "y" : "ies"} selected`}
            </span>
            <svg className={`w-4 h-4 text-[#8b949e] transition-transform ${isDropdownOpen ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
           </button>

           {isDropdownOpen && (
            <div className="absolute z-[999] left-0 right-0 mt-1 bg-white border border-[#d0d7de] rounded-lg shadow-xl max-h-72 flex flex-col">
             {/* Search input */}
             <div className="p-2 border-b border-[#e5e7eb] flex-shrink-0">
              <input type="text" placeholder="Search competencies…"
               value={searchTerm}
               onChange={(e) => setGradeCompSearch((prev) => ({ ...prev, [grade.grade_code]: e.target.value }))}
               className="w-full bg-[#f6f8fa] border border-[#d0d7de] rounded px-2.5 py-1.5 text-xs text-[#24292f] focus:outline-none focus:ring-1 focus:ring-[#0078d4] focus:border-[#0078d4]"
               autoFocus />
             </div>
             {/* Options list — single scroll area */}
             <div className="overflow-y-auto flex-1 min-h-0">
              {isLoading ? (
               <div className="px-3 py-4 text-center text-xs text-[#8b949e]">
                <div className="inline-block w-4 h-4 border-2 border-[#0078d4] border-t-transparent rounded-full animate-spin mr-2"></div>
                Loading…
               </div>
              ) : error ? (
               <div className="px-3 py-4 text-center text-xs text-red-500">
                {error}
                <button onClick={() => loadCompetenciesForGrade(grade.grade_code, form.state_id)} className="ml-2 text-[#0078d4] hover:underline">Retry</button>
               </div>
              ) : filteredOptions.length === 0 ? (
               <div className="px-3 py-4 text-center text-xs text-[#8b949e] italic">
                {options.length === 0 ? "No competencies available for this grade" : "No matches found"}
               </div>
              ) : (
               filteredOptions.map((comp) => {
                const isSelected = selectedIds.has(comp.code);
                return (
                 <button key={comp.code} type="button"
                  onClick={() => toggleCompetencyForGrade(grade.grade_code, comp)}
                  className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-[#f6f8fa] transition-colors ${isSelected ? "bg-blue-50" : ""}`}>
                  <span className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${isSelected ? "bg-[#0078d4] border-[#0078d4]" : "border-[#d0d7de]"}`}>
                   {isSelected && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                  </span>
                  <span className="font-medium text-[#24292f]">{comp.code}</span>
                  <span className="text-[#8b949e]">—</span>
                  <span className="text-[#57606a] truncate">{comp.name}</span>
                  {comp.sub_header && <span className="ml-auto text-[10px] text-[#8b949e] truncate max-w-[120px]">{comp.sub_header}</span>}
                 </button>
                );
               })
              )}
             </div>
             {/* Footer with count */}
             {options.length > 0 && (
              <div className="px-3 py-2 border-t border-[#e5e7eb] bg-[#f6f8fa] text-[11px] text-[#8b949e] flex justify-between flex-shrink-0">
               <span>{options.length} available</span>
               <span>{grade.competencies.length} selected</span>
              </div>
             )}
            </div>
           )}
          </div>

          {/* Selected competencies list */}
          {grade.competencies.length === 0 ? (
           <p className="text-xs text-[#8b949e] italic">No competencies selected yet.</p>
          ) : (() => {
           const totalCount = grade.competencies.length;
           const orderCounts = {};
           grade.competencies.forEach((cc) => {
            const o = parseInt(cc.competency_order, 10);
            if (o >= 1) orderCounts[o] = (orderCounts[o] || 0) + 1;
           });
           return (
           <div className="flex flex-wrap gap-2">
            {[...grade.competencies]
             .sort((a, b) => {
              const oa = parseInt(a.competency_order, 10);
              const ob = parseInt(b.competency_order, 10);
              return (isNaN(oa) ? Infinity : oa) - (isNaN(ob) ? Infinity : ob);
             })
             .map((c) => {
              const ord = parseInt(c.competency_order, 10);
              const isValidOrder = ord >= 1 && ord <= totalCount && orderCounts[ord] === 1;
              // The highest number you're allowed to assign right now is one past however many
              // OTHER competencies in this grade already have a valid order — this is what
              // stops you from skipping ahead (e.g. typing "3" before "1" and "2" exist).
              const othersAssignedCount = new Set(
               grade.competencies
                .filter((cc) => cc.competency_code !== c.competency_code)
                .map((cc) => parseInt(cc.competency_order, 10))
                .filter((o) => o >= 1)
              ).size;
              const nextExpected = othersAssignedCount + 1;
              const orderTitle = !(ord >= 1)
               ? `Next available order: ${nextExpected}`
               : orderCounts[ord] > 1
               ? `Order ${ord} is used more than once — each order must be unique`
               : ord > totalCount
               ? `Orders must run consecutively from 1 to ${totalCount} (no gaps)`
               : "Competency order";
              return (
             <div key={c.competency_code}
              className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-800 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-blue-200">
              <HiOutlineCheckCircle className="text-blue-500 flex-shrink-0" />
              <span className="font-semibold">{c.competency_code}</span>
              {c.competency_name && <span className="text-blue-600">— {c.competency_name}</span>}
              <input
               type="number"
               min={1}
               max={totalCount}
               placeholder={`Order (${nextExpected})`}
               value={c.competency_order || ""}
               onChange={(e) => {
                const raw = e.target.value;
                const parsed = raw === "" ? null : Number.parseInt(raw, 10);
                // Reject skipping ahead of the next sequential number (or going below 1) by
                // snapping the controlled input back to its previous value instead of just
                // ignoring the event — otherwise the native input would visually show the
                // rejected digits even though React's state never changed.
                const rejected = raw !== "" && (Number.isNaN(parsed) || parsed > nextExpected || parsed < 1);
                const nextValue = rejected ? (c.competency_order || "") : raw;
                setForm({
                 ...form,
                 grades: form.grades.map((g) => {
                  if (g.grade_code !== grade.grade_code) return g;
                  return {
                   ...g,
                   competencies: g.competencies.map((comp) =>
                    comp.competency_code === c.competency_code
                     ? { ...comp, competency_order: nextValue }
                     : comp
                   ),
                  };
                 }),
                });
               }}
               className={`w-16 bg-white border rounded px-1.5 py-0.5 text-xs text-center focus:outline-none focus:ring-1 focus:ring-[#0078d4] ${
                isValidOrder ? "border-blue-200" : "border-red-300"
               }`}
               title={orderTitle}
              />
              <button onClick={() => removeCompetencyFromGrade(grade.grade_code, c.competency_code)}
               className="ml-0.5 text-blue-400 hover:text-red-500 transition-colors" title="Remove">
               <HiOutlineX className="text-sm" />
              </button>
             </div>
              );
             })}
           </div>
           );
          })()}
         </div>
        </div>
        );
      })}

      {form.grades.length === 0 && (
       <p className="text-xs text-[#8b949e] italic">No grades added yet. Select a grade above to begin.</p>
      )}
      <FieldError field="grades" />
     </fieldset>

     {/* ── 4a. Previous Round Students (shown during create for round > 1, continuous only) ── */}
     {cycleType === "continuous" && form.round_number && parseInt(form.round_number, 10) > 1 && (
      <fieldset className="mb-6">
       <legend className="text-sm font-semibold text-[#24292f] mb-3">
        Skip Configuration (Round {parseInt(form.round_number, 10) - 1} Categories)
       </legend>
       <p className="text-xs text-[#8b949e] mb-3">
        Select which student categories to skip for this round. All students in selected categories will be marked as skipped.
       </p>
       {prevRoundLoading ? (
        <p className="text-xs text-[#8b949e] italic">Loading previous round data…</p>
       ) : prevRoundData && prevRoundData.categories && prevRoundData.categories.length > 0 ? (
        <div className="space-y-3">
         <p className="text-xs text-[#57606a]">
          From cycle: <strong>{prevRoundData.previous_cycle_title || `Round ${prevRoundData.previous_round}`}</strong>
          {" "}— {prevRoundData.total_students} student{prevRoundData.total_students !== 1 ? "s" : ""}
         </p>
         {prevRoundData.categories.map((group) => {
          const catName = group.category_name;
          const isCatSkipped = !!skippedCategories[catName];
          return (
          <div key={catName} className={`border rounded-lg overflow-hidden ${isCatSkipped ? "border-red-300 bg-red-50/30" : "border-[#e5e7eb]"}`}>
           <div className="flex items-center justify-between px-3 py-2.5 bg-[#f6f8fa]">
            <div className="flex items-center gap-2">
             <input type="checkbox" checked={isCatSkipped}
              onChange={() => toggleSkipCategory(catName)}
              className="rounded border-[#d0d7de]" />
             <span className="w-3 h-3 rounded-full" style={{ backgroundColor: group.color || "#6b7280" }} />
             <span className="text-xs font-semibold text-[#24292f]">
              {catName} (Threshold {OP_SYMBOL[group.operator || "gte"]} {group.threshold})
             </span>
            </div>
            <div className="flex items-center gap-2">
             <span className="text-xs text-[#57606a] font-medium">{group.student_count} student{group.student_count !== 1 ? "s" : ""}</span>
             <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${isCatSkipped ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}>
              {isCatSkipped ? "Skipped" : "Included"}
             </span>
            </div>
           </div>
          </div>
          );
         })}
         {/* Summary */}
         <div className="bg-[#f6f8fa] rounded-lg px-3 py-2 text-xs text-[#57606a]">
          <strong>Summary:</strong>{" "}
          {(() => {
           let included = 0, skipped = 0;
           prevRoundData.categories.forEach((cat) => {
            if (skippedCategories[cat.category_name]) skipped += cat.student_count;
            else included += cat.student_count;
           });
           return <>{included} included, {skipped} skipped</>;
          })()}
         </div>
        </div>
       ) : prevRoundData ? (
        <p className="text-xs text-[#8b949e] italic">No student data available from the previous round.</p>
       ) : !form.state_id ? (
        <p className="text-xs text-[#8b949e] italic">Select a state to see previous round students.</p>
       ) : (
        <p className="text-xs text-[#8b949e] italic">No previous round cycle found for this state.</p>
       )}
      </fieldset>
     )}

     {/* Read-only reference: currently selected competencies and their total
         weight. previousRoundWeight loads separately (prevRoundLoading) so
         this renders immediately with the current round's own weight; the
         previous-round line below shows its own "Loading…" placeholder
         rather than blocking the whole form. */}
     {form.state_id && (
      <div className="border border-[#e5e7eb] rounded-lg overflow-hidden mb-4">
       <div className="px-3 py-2 bg-[#f6f8fa] border-b border-[#e5e7eb]">
        <span className="text-xs font-semibold text-[#24292f]">Selected Competencies Weightage</span>
        <span className="text-[11px] text-[#8b949e] ml-2">Use this to decide sensible student-category thresholds.</span>
       </div>
       <div className="p-3">
        {selectedCompWeightage.length > 0 ? (
         <table className="w-full text-xs border border-[#f0f0f0] rounded">
          <thead>
           <tr className="bg-[#fafbfc]">
            <th className="text-left px-2 py-1.5 font-medium text-[#8b949e]">Competency</th>
            <th className="text-left px-2 py-1.5 font-medium text-[#8b949e]">Category</th>
            <th className="text-right px-2 py-1.5 font-medium text-[#8b949e]">Weight</th>
           </tr>
          </thead>
          <tbody>
           {selectedCompWeightage.map((r) => (
            <tr key={r.code} className="border-t border-[#f0f0f0]">
             <td className="px-2 py-1.5 text-[#24292f]">{r.name}</td>
             <td className="px-2 py-1.5 text-[#57606a]">{r.category_name}</td>
             <td className="px-2 py-1.5 text-right text-[#57606a]">{r.weight}</td>
            </tr>
           ))}
          </tbody>
          <tfoot>
           {prevRoundLoading ? (
            <tr className="border-t border-[#f0f0f0] text-[#8b949e] italic">
             <td className="px-2 py-1.5" colSpan={3}>Loading previous round weight…</td>
            </tr>
           ) : previousRoundWeight > 0 && (
            <tr className="border-t border-[#f0f0f0] text-[#8b949e]">
             <td className="px-2 py-1.5" colSpan={2}>Previous round weight (carried into cumulative score)</td>
             <td className="px-2 py-1.5 text-right">{previousRoundWeight}</td>
            </tr>
           )}
           <tr className="border-t border-[#e5e7eb] bg-[#fafbfc] font-semibold">
            <td className="px-2 py-1.5 text-[#24292f]" colSpan={2}>
             {prevRoundLoading ? "Total weight (this round; previous pending)" : previousRoundWeight > 0 ? "Total weight (this round + previous)" : "Total weight"}
            </td>
            <td className="px-2 py-1.5 text-right text-[#24292f]">{prevRoundLoading ? currentRoundWeight : maxPossibleScore}</td>
           </tr>
          </tfoot>
         </table>
        ) : (
         <p className="text-xs text-[#8b949e] italic">Select competencies in the grades above to see their weightage.</p>
        )}
       </div>
      </div>
     )}

     {/* ── 4b. Student Categories ── */}
      <fieldset className="mb-6">
       <legend className="text-sm font-semibold text-[#24292f] mb-3">Student Categories</legend>

       {/* Add/Edit category row */}
       <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 mb-3">
        <div>
         <label className="block text-[11px] text-[#8b949e] mb-0.5">Category Name</label>
         <input type="text" placeholder="e.g. A" maxLength={50} value={catForm.name}
          onChange={(e) => setCatForm({ ...catForm, name: e.target.value })}
          className={inputClass} />
        </div>
        <div>
         <label className="block text-[11px] text-[#8b949e] mb-0.5">Comparison</label>
         <select value={catForm.operator} onChange={(e) => setCatForm({ ...catForm, operator: e.target.value })}
          className={selectClass}>
          {OP_OPTIONS.map((o) => (
           <option key={o.value} value={o.value}>{o.label}</option>
          ))}
         </select>
         <p className="text-[10px] text-[#8b949e] mt-0.5">How a student's score is compared to the threshold.</p>
        </div>
        <div>
         <label className="block text-[11px] text-[#8b949e] mb-0.5">Threshold (absolute integer)</label>
         <input type="number" min={0} step={1} max={maxPossibleScore || undefined} placeholder="e.g. 5" value={catForm.threshold}
          onChange={(e) => setCatForm({ ...catForm, threshold: e.target.value })}
          className={inputClass} />
         <p className="text-[10px] text-[#8b949e] mt-0.5">
          Score is matched as: score {OP_SYMBOL[catForm.operator || "gte"]} threshold.
          {maxPossibleScore > 0 && <> Max allowed: {maxPossibleScore}.</>}
         </p>
        </div>
        <div>
         <label className="block text-[11px] text-[#8b949e] mb-0.5">Color</label>
         <select value={catForm.color} onChange={(e) => setCatForm({ ...catForm, color: e.target.value })}
          className={selectClass}>
          {CAT_COLORS.map((c) => (
           <option key={c.value} value={c.value}>{c.label}</option>
          ))}
         </select>
        </div>
       </div>
       <div className="flex items-center gap-2 mb-4">
        <button onClick={handleSaveCategory} type="button"
         disabled={!catForm.name.trim() || catForm.threshold === ""}
         className="px-4 py-2 bg-[#0078d4] text-white text-xs font-medium rounded-lg hover:bg-[#106ebe] disabled:opacity-40">
         {editingCatId ? <><HiOutlinePencil className="inline -mt-0.5 mr-1" />Update Category</> : <><HiOutlinePlus className="inline -mt-0.5 mr-1" />Add Category</>}
        </button>
        {editingCatId && (
         <button onClick={cancelEditCategory} type="button"
          className="px-4 py-2 text-xs font-medium text-[#24292f] bg-white border border-[#d0d7de] rounded-lg hover:bg-[#f3f4f6]">
          Cancel
         </button>
        )}
       </div>

       {/* Existing categories table */}
       {studentCategories.length > 0 ? (
        <div className="border border-[#e5e7eb] rounded-lg overflow-hidden">
         <table className="w-full text-xs">
          <thead>
           <tr className="bg-[#f6f8fa]">
            <th className="text-left px-3 py-2">Category</th>
            <th className="text-left px-3 py-2">Threshold</th>
            <th className="text-left px-3 py-2">Color</th>
            <th className="px-3 py-2 w-20"></th>
           </tr>
          </thead>
          <tbody>
           {studentCategories.map((cat) => (
            <tr key={cat.id} className={`border-t border-[#f0f0f0] ${editingCatId === cat.id ? "bg-blue-50" : ""}`}>
             <td className="px-3 py-2 font-semibold">{cat.name}</td>
             <td className="px-3 py-2">{OP_SYMBOL[cat.operator || "gte"]} {cat.threshold}</td>
             <td className="px-3 py-2">
              <span className="inline-block w-4 h-4 rounded border border-[#e5e7eb]" style={{ backgroundColor: cat.color || "#9ca3af" }} />
             </td>
             <td className="px-3 py-2 text-right whitespace-nowrap">
              <button onClick={() => startEditCategory(cat)} title="Edit" className="text-[#0078d4] hover:text-[#106ebe] mr-1.5">
               <HiOutlinePencil />
              </button>
              <button onClick={() => handleDeleteCategory(cat.id)} title="Delete" className="text-red-500 hover:text-red-700">
               <HiOutlineTrash />
              </button>
             </td>
            </tr>
           ))}
          </tbody>
         </table>
        </div>
       ) : (
        <p className="text-xs text-[#8b949e] italic">No categories yet. Add a category with a name and threshold above.</p>
       )}
      </fieldset>

     {/* ── 5. Schools (quarterly = non-adopted table, continuous = note only) ── */}
     {cycleType === "quarterly" && (
      <fieldset className="mb-6">
       <legend className="text-sm font-semibold text-[#24292f] mb-3">Schools</legend>
       <FieldError field="school_ids" />

       {!form.state_id ? (
        <p className="text-xs text-[#8b949e] italic">Select a state above to see available schools.</p>
       ) : (
        <>
         {/* Action bar: Search + Upload + Delete selected */}
         <div className="flex flex-wrap items-center gap-2 mb-3">
          <input type="text" placeholder="Search schools…" value={schoolSearch}
           onChange={(e) => setSchoolSearch(e.target.value)}
           className="flex-1 min-w-[180px] bg-[#f6f8fa] border border-[#d0d7de] rounded-lg px-3 py-2 text-xs text-[#24292f] focus:outline-none focus:ring-1 focus:ring-[#0078d4]" />
          <button type="button"
           onClick={() => { setSchoolUploadFile(null); setSchoolUploadResult(null); setShowSchoolUploadModal(true); }}
           className="inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold rounded-lg cursor-pointer transition-all duration-200 bg-white text-[#24292f] border border-[#d0d5dd] hover:bg-[#f3f4f6] hover:border-[#8b949e]">
           <HiOutlineUpload className="text-sm" /> Upload Schools
          </button>
          {selectedSchoolCount > 0 && (
           <button type="button"
            onClick={() => {
              // Empty the list (same as pressing ✕ on each), drop uploaded
              // schools, and clear the selection — saved schools go on save.
              resetSchoolChanges();
              setHideAllSchools(true);
              if (editingId) setSchoolClearAll(true);
            }}
            className="px-3 py-2 text-xs font-medium text-red-600 border border-red-300 rounded-lg hover:bg-red-50 flex items-center gap-1">
            <HiOutlineTrash className="text-sm" />Remove All ({selectedSchoolCount.toLocaleString()})
           </button>
          )}
         </div>

         {/* Schools table — server rows one page at a time, uploaded rows after them */}
         {schoolsLoading && schoolRows.length === 0 ? (
          <div className="text-center py-8 text-xs text-[#8b949e]">
           <div className="inline-block w-4 h-4 border-2 border-[#0078d4] border-t-transparent rounded-full animate-spin mr-2"></div>Loading schools…
          </div>
         ) : (() => {
          const search = debouncedSchoolSearch;
          const serverTotal = hideAllSchools ? 0 : schoolTotal;
          const hiddenMatching = hideAllSchools ? 0 : [...hiddenSchools.values()].filter((r) => rowMatchesSearch(r, search)).length;
          const stagedFiltered = stagedSchools
           .map((st, i) => ({ ...st, _stagedIdx: i }))
           .filter((st) => st.is_new && rowMatchesSearch(st, search));
          const shownCount = Math.max(0, serverTotal - hiddenMatching) + stagedFiltered.length;
          const totalItems = serverTotal + stagedFiltered.length;
          const totalSchoolPages = Math.max(1, Math.ceil(totalItems / SCHOOL_PAGE_SIZE));
          // Server rows fill pages first; uploaded rows continue after the last one
          const serverOnPage = Math.min(SCHOOL_PAGE_SIZE, Math.max(0, serverTotal - (schoolPage - 1) * SCHOOL_PAGE_SIZE));
          const stagedStart = Math.max(0, (schoolPage - 1) * SCHOOL_PAGE_SIZE - serverTotal);
          const pageServerRows = hideAllSchools ? [] : schoolRows.filter((r) => !hiddenSchools.has(r.id));
          const pageStagedRows = stagedFiltered.slice(stagedStart, stagedStart + (SCHOOL_PAGE_SIZE - serverOnPage));
          const setStagedSelected = (idx, selected) =>
           setStagedSchools((prev) => prev.map((st, i) => (i === idx ? { ...st, selected } : st)));
          const headerChecked = (serverTotal > 0 || stagedFiltered.length > 0)
           && (serverTotal === 0 || allMatchingSelected)
           && stagedFiltered.every((st) => st.selected !== false);
          if (shownCount === 0 && !schoolsLoading) {
           return (
            <p className="text-xs text-[#8b949e] italic py-4 text-center">
             {!search ? "No schools found. Use Upload to add schools." : "No matches found."}
            </p>
           );
          }
          return (
           <div className="border border-[#e5e7eb] rounded-lg overflow-hidden">
            <table className="w-full text-xs">
             <thead>
              <tr className="bg-[#f6f8fa] border-b border-[#e5e7eb]">
               <th className="px-3 py-2 w-8">
                <input type="checkbox" checked={headerChecked}
                 onChange={() => {
                  if (serverTotal > 0) {
                   toggleAllMatching();
                  } else {
                   const selecting = !headerChecked;
                   setStagedSchools((prev) => prev.map((st) => (st.is_new && rowMatchesSearch(st, search) ? { ...st, selected: selecting } : st)));
                  }
                 }}
                 className="rounded border-[#d0d7de]" />
               </th>
               <th className="text-left px-3 py-2 font-semibold text-[#24292f]">School Name</th>
               <th className="text-left px-3 py-2 font-semibold text-[#24292f]">UDISE Code</th>
               <th className="text-left px-3 py-2 font-semibold text-[#24292f]">Block</th>
               <th className="text-left px-3 py-2 font-semibold text-[#24292f]">District</th>
               <th className="text-left px-3 py-2 font-semibold text-[#24292f]">Status</th>
               <th className="px-3 py-2 w-10 font-semibold text-[#24292f]">Action</th>
              </tr>
             </thead>
             <tbody>
              {[
               ...pageServerRows.map((r) => ({ row: r, staged: false })),
               ...pageStagedRows.map((r) => ({ row: r, staged: true })),
              ].map(({ row: s, staged }) => {
               const isSelected = staged ? s.selected !== false : isSchoolSelected(s);
               const toggle = () => (staged ? setStagedSelected(s._stagedIdx, !isSelected) : setSchoolsSelected([s], !isSelected));
               return (
                <tr key={staged ? `staged_${s._stagedIdx}` : `db_${s.id}`} onClick={toggle}
                 className={`border-t border-[#f0f0f0] cursor-pointer transition-colors ${isSelected ? "bg-blue-50 hover:bg-blue-100" : "hover:bg-[#f6f8fa]"}`}>
                 <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" checked={isSelected} onChange={toggle} className="rounded border-[#d0d7de]" />
                 </td>
                 <td className="px-3 py-2 font-medium text-[#24292f]">{s.name}</td>
                 <td className="px-3 py-2 text-[#57606a]">{s.udise_code || "—"}</td>
                 <td className="px-3 py-2 text-[#57606a]">{s.block_name || "—"}</td>
                 <td className="px-3 py-2 text-[#57606a]">{s.district_name || "—"}</td>
                 <td className="px-3 py-2">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${s.is_active !== false ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                   {s.is_active !== false ? "Active" : "Inactive"}
                  </span>
                 </td>
                 <td className="px-3 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                  <button
                   onClick={() => {
                    if (staged) {
                     // Remove from staged list
                     setStagedSchools((prev) => prev.filter((_, i) => i !== s._stagedIdx));
                    } else {
                     // Hide from the list and deselect
                     setSchoolsSelected([s], false);
                     setHiddenSchools((prev) => new Map(prev).set(s.id, s));
                    }
                   }}
                   className="text-red-400 hover:text-red-600 transition-colors" title={staged ? "Remove uploaded school" : "Remove from list"}>
                   <HiOutlineX className="text-sm" />
                  </button>
                 </td>
                </tr>
               );
              })}
             </tbody>
            </table>
            <div className="px-3 py-2 bg-[#f6f8fa] border-t border-[#e5e7eb] text-[11px] text-[#8b949e] flex justify-between">
             <span>{shownCount.toLocaleString()} school{shownCount !== 1 ? "s" : ""} shown{stagedSchools.length > 0 ? ` (${stagedSchools.length} uploaded)` : ""}</span>
             <span>{selectedSchoolCount.toLocaleString()} selected</span>
            </div>
            {totalItems > SCHOOL_PAGE_SIZE && (
             <Pagination
              currentPage={Math.min(schoolPage, totalSchoolPages)}
              totalPages={totalSchoolPages}
              totalCount={totalItems}
              pageSize={SCHOOL_PAGE_SIZE}
              onPageChange={setSchoolPage}
              entityName="schools"
             />
            )}
           </div>
          );
         })()}
        </>
       )}
      </fieldset>
     )}
     {/* ── 6. Reminders ── */}
     <fieldset className="mb-6">
       <legend className="text-sm font-semibold text-[#24292f] mb-3">Reminders</legend>

       {/* Add-reminder row */}
       <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 mb-3">
        <div>
         <label className="block text-[11px] text-[#8b949e] mb-0.5">Send after (days)</label>
         <input type="number" min="1" placeholder="e.g. 10" value={reminderForm.when_value}
          onChange={(e) => setReminderForm({ ...reminderForm, when_value: e.target.value })}
          onKeyDown={blockNonDigitKey}
          className={inputClass} />
         {(() => {
          const total = cycleLengthInDays(form.start_date, form.end_date);
          const day = parseInt(reminderForm.when_value, 10);
          if (reminderInputError?.field === "when") {
           return <p className="text-[10px] text-red-600 mt-0.5">{reminderInputError.message}</p>;
          }
          if (reminderConflict?.blocking) {
           return <p className="text-[10px] text-red-600 mt-0.5">{reminderConflict.message}</p>;
          }
          if (total != null && day > total) {
           return <p className="text-[10px] text-amber-600 mt-0.5">Cycle is only {total} days long</p>;
          }
          if (reminderConflict) {
           return <p className="text-[10px] text-amber-600 mt-0.5">{reminderConflict.message}</p>;
          }
          return (
           <p className="text-[10px] text-[#8b949e] mt-0.5">
            Counted from the cycle start{total != null ? ` — cycle is ${total} days` : ""}
           </p>
          );
         })()}
        </div>
        <div>
         <label className="block text-[11px] text-[#8b949e] mb-0.5">Minimum completion (%)</label>
         <input type="number" min="1" max="100" placeholder="e.g. 40" value={reminderForm.threshold_value}
          onChange={(e) => setReminderForm({ ...reminderForm, threshold_value: e.target.value })}
          onKeyDown={blockNonDigitKey}
          className={inputClass} />
         {reminderInputError?.field === "threshold" ? (
          <p className="text-[10px] text-red-600 mt-0.5">{reminderInputError.message}</p>
         ) : (
          <p className="text-[10px] text-[#8b949e] mt-0.5">Reminder is sent if completion is below this</p>
         )}
        </div>
        <div>
         <label className="block text-[11px] text-[#8b949e] mb-0.5">Mode</label>
         <div className="flex items-center gap-3 pt-1.5">
          {[["in_app", "In-App"], ["email", "Email"]].map(([val, label]) => (
           <label key={val} className="flex items-center gap-1 text-xs text-[#24292f] cursor-pointer select-none">
            <input
             type="checkbox"
             checked={reminderForm.mode.includes(val)}
             onChange={(e) => setReminderForm({
              ...reminderForm,
              mode: e.target.checked
               ? [...reminderForm.mode, val]
               : reminderForm.mode.filter((m) => m !== val),
             })}
            />
            {label}
           </label>
          ))}
         </div>
        </div>
       </div>
       <div className="flex items-center gap-2 mb-4">
        <button onClick={handleSaveReminder} type="button"
         disabled={!reminderForm.when_value || !reminderForm.threshold_value || reminderForm.mode.length === 0 || reminderConflict?.blocking || !!reminderInputError}
         className="px-4 py-2 bg-[#0078d4] text-white text-xs font-medium rounded-lg hover:bg-[#106ebe] disabled:opacity-40">
         {editingReminderId ? <><HiOutlinePencil className="inline -mt-0.5 mr-1" />Update Reminder</> : <><HiOutlinePlus className="inline -mt-0.5 mr-1" />Add Reminder</>}
        </button>
        {editingReminderId && (
         <button onClick={cancelEditReminder} type="button"
          className="px-4 py-2 text-xs font-medium text-[#24292f] bg-white border border-[#d0d7de] rounded-lg hover:bg-[#f3f4f6]">
          Cancel
         </button>
        )}
       </div>

       {/* Reminders table */}
       {(() => {
        const displayReminders = editingId ? reminders : pendingReminders;
        return displayReminders.length > 0 ? (
         <div className="border border-[#e5e7eb] rounded-lg overflow-hidden">
          <table className="w-full text-xs">
           <thead>
            <tr className="bg-[#f6f8fa]">
             <th className="text-left px-3 py-2">Reminder</th>
             <th className="text-left px-3 py-2">Mode</th>
             <th className="px-3 py-2 w-20"></th>
            </tr>
           </thead>
           <tbody>
            {displayReminders.map((r) => (
             <tr key={r.id} className={`border-t border-[#f0f0f0] ${editingReminderId === r.id ? "bg-blue-50" : ""}`}>
              <td className="px-3 py-2">{describeReminder(r, form.start_date, form.end_date)}</td>
              <td className="px-3 py-2">{formatReminderMode(r.mode)}</td>
              <td className="px-3 py-2 text-right whitespace-nowrap">
               <button onClick={() => startEditReminder(r)} title="Edit" className="text-[#0078d4] hover:text-[#106ebe] mr-1.5">
                <HiOutlinePencil />
               </button>
               <button onClick={() => handleDeleteReminder(r.id)} title="Delete" className="text-red-500 hover:text-red-700">
                <HiOutlineTrash />
               </button>
              </td>
             </tr>
            ))}
           </tbody>
          </table>
         </div>
        ) : (
         <p className="text-xs text-[#8b949e] italic">No reminders yet. Add one above.</p>
        );
       })()}
      </fieldset>

     {/* ── Actions (bottom-right aligned, consistent spacing) ── */}
     <div className="flex justify-end gap-3 pt-4 border-t border-[#e5e7eb]">
      <button onClick={() => setView("list")} className="px-4 py-2 text-sm font-medium text-[#24292f] bg-white border border-[#d0d7de] rounded-lg hover:bg-[#f3f4f6]">Cancel</button>
      <button onClick={handleSave} disabled={saving || hasErrors}
       className="px-5 py-2 text-sm font-medium text-white bg-[#0078d4] rounded-lg hover:bg-[#106ebe] disabled:opacity-40">
       {saving ? "Saving…" : editingId ? "Update" : "Create"}
      </button>
     </div>
      </>
     )}
     </div>
    </div>

    {/* Delete confirm */}
    {showDeleteConfirm && (
     <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-5 text-center">
       <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-red-100 flex items-center justify-center">
        <HiOutlineExclamation className="text-2xl text-red-600" />
       </div>
       <h3 className="text-base font-semibold text-[#24292f] mb-1">Delete Cycle?</h3>
       <p className="text-sm text-[#57606a] mb-4">
        Delete {showDeleteConfirm.title || showDeleteConfirm.cycle_type}? This cannot be undone.
       </p>
       <div className="flex justify-center gap-2">
        <button onClick={() => setShowDeleteConfirm(null)} className="px-4 py-2 text-sm font-medium text-[#24292f] bg-white border border-[#d0d7de] rounded-lg hover:bg-[#f3f4f6]">Cancel</button>
        <button onClick={() => handleDelete(showDeleteConfirm.id)} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700">Delete</button>
       </div>
      </div>
     </div>
    )}

    {/* Publish confirm */}
    {showPublishConfirm && (
     <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-5 text-center">
       <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-green-100 flex items-center justify-center">
        <HiOutlineCheckCircle className="text-2xl text-green-600" />
       </div>
       <h3 className="text-base font-semibold text-[#24292f] mb-1">Publish Cycle?</h3>
       <p className="text-sm text-[#57606a] mb-4">
        Are you sure you want to publish {showPublishConfirm.title || showPublishConfirm.cycle_type}?
       </p>
       <div className="flex justify-center gap-2">
        <button onClick={() => setShowPublishConfirm(null)} className="px-4 py-2 text-sm font-medium text-[#24292f] bg-white border border-[#d0d7de] rounded-lg hover:bg-[#f3f4f6]">Cancel</button>
        <button onClick={() => handlePublish(showPublishConfirm.id)} className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700">Publish</button>
       </div>
      </div>
     </div>
    )}

    {/* Unpublish confirm */}
    {showUnpublishConfirm && (
     <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-5 text-center">
       <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-yellow-100 flex items-center justify-center">
        <HiOutlineExclamation className="text-2xl text-yellow-600" />
       </div>
       <h3 className="text-base font-semibold text-[#24292f] mb-1">Unpublish Cycle?</h3>
       <p className="text-sm text-[#57606a] mb-4">
        Are you sure you want to unpublish {showUnpublishConfirm.title || showUnpublishConfirm.cycle_type}?
       </p>
       <div className="flex justify-center gap-2">
        <button onClick={() => setShowUnpublishConfirm(null)} className="px-4 py-2 text-sm font-medium text-[#24292f] bg-white border border-[#d0d7de] rounded-lg hover:bg-[#f3f4f6]">Cancel</button>
        <button onClick={() => handleUnpublish(showUnpublishConfirm.id)} className="px-4 py-2 text-sm font-medium text-white bg-yellow-500 rounded-lg hover:bg-yellow-600">Unpublish</button>
       </div>
      </div>
     </div>
    )}

    {/* Complete confirm */}
    {showCompleteConfirm && (
     <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-5 text-center">
       <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-orange-100 flex items-center justify-center">
        <HiOutlineExclamation className="text-2xl text-orange-600" />
       </div>
       <h3 className="text-base font-semibold text-[#24292f] mb-1">Complete Cycle?</h3>
       <p className="text-sm text-[#57606a] mb-4">
        Are you sure you want to mark <strong>{showCompleteConfirm.title || showCompleteConfirm.cycle_type}</strong> as complete? This cannot be undone.
       </p>
       <div className="flex justify-center gap-2">
        <button onClick={() => setShowCompleteConfirm(null)} className="px-4 py-2 text-sm font-medium text-[#24292f] bg-white border border-[#d0d7de] rounded-lg hover:bg-[#f3f4f6]">Cancel</button>
        <button onClick={() => handleComplete(showCompleteConfirm.id)} className="px-4 py-2 text-sm font-medium text-white bg-orange-500 rounded-lg hover:bg-orange-600">Complete</button>
       </div>
      </div>
     </div>
    )}

    {/* ── School Bulk Upload Modal (matches Master Data pattern) ── */}
    {showSchoolUploadModal && (
     <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[200] p-4"
      onClick={() => !schoolUploadLoading && setShowSchoolUploadModal(false)}>
      <div className="bg-white rounded-lg border border-[#e5e7eb] w-full max-w-md shadow-[0_8px_24px_rgba(0,0,0,0.12)] animate-[fadeIn_0.2s_ease-out]"
       role="dialog" aria-modal="true"
       onClick={(e) => e.stopPropagation()}
       onKeyDown={(e) => { if (e.key === "Escape" && !schoolUploadLoading) setShowSchoolUploadModal(false); }}>

       {/* Header */}
       <div className="flex justify-between items-center px-6 py-4 border-b border-[#e5e7eb]">
        <div>
         <h3 className="m-0 text-base font-semibold text-[#24292f]">Bulk Upload Schools</h3>
         <p className="m-0 mt-0.5 text-xs text-[#8b949e]">Upload an Excel file (.xlsx) to import schools</p>
        </div>
        <button className="w-8 h-8 flex items-center justify-center rounded-md text-[#8b949e] hover:text-[#57606a] hover:bg-[#f3f4f6] transition-all duration-200 border-none bg-transparent cursor-pointer"
         onClick={() => setShowSchoolUploadModal(false)} disabled={schoolUploadLoading}>
         <HiOutlineX className="text-lg" />
        </button>
       </div>

       {/* Body */}
       <div className="p-6 space-y-5">
        {/* Template Download Card */}
        <div className="flex items-center gap-3 p-3 bg-[#eff6ff] rounded-md border border-[#d0d5dd]">
         <HiOutlineDocumentText className="text-lg text-[#0078d4] flex-shrink-0" />
         <div className="flex-1">
          <p className="m-0 text-sm font-medium text-[#24292f]">Need a template?</p>
          <p className="m-0 text-xs text-[#57606a] mt-0.5">Required: name, udise_code, block_name. Optional: latitude, longitude</p>
         </div>
         <button className="inline-flex items-center gap-1 py-1.5 px-3 text-xs font-semibold rounded-lg cursor-pointer transition-all duration-200 bg-white text-[#0078d4] border border-[#d0d5dd] hover:bg-[#f3f4f6]"
          onClick={() => {
           const { downloadXlsxTemplate } = require("../services/xlsxTemplate");
           downloadXlsxTemplate("schools_template.xlsx", ["name", "udise_code", "block_name", "latitude", "longitude"], [["ABC Public School", "09010100101", "Sadar", "26.8467", "80.9462"], ["XYZ Academy", "09010100102", "Gomti Nagar", "26.8505", "80.9553"]]);
          }}>
          <HiOutlineDownload className="text-sm" /> Download
         </button>
        </div>

        {/* File Drop Zone */}
        <div>
         <label className="block mb-2 text-sm font-medium text-[#24292f]">Excel File (.xlsx)</label>
         <div className="relative border-2 border-dashed border-[#d0d5dd] rounded-md p-6 text-center hover:border-[#0078d4] transition-colors cursor-pointer"
          onClick={() => schoolFileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onDrop={(e) => {
           e.preventDefault(); e.stopPropagation();
           const f = e.dataTransfer.files[0];
           if (f && !f.name.endsWith(".xlsx")) {
            setToast({ message: "Only .xlsx files are accepted", type: "error" });
            return;
           }
           setSchoolUploadFile(f || null);
           setSchoolUploadResult(null);
          }}>
          <input ref={schoolFileInputRef} type="file" accept=".xlsx" className="hidden"
           onChange={(e) => {
            const f = e.target.files[0];
            if (f && !f.name.endsWith(".xlsx")) {
             setToast({ message: "Only .xlsx files are accepted", type: "error" });
             return;
            }
            setSchoolUploadFile(f || null);
            setSchoolUploadResult(null);
           }} />
          <HiOutlineUpload className="text-2xl text-[#8b949e] mx-auto mb-2" />
          {schoolUploadFile
           ? <p className="m-0 text-sm font-medium text-[#24292f]">{schoolUploadFile.name}</p>
           : <p className="m-0 text-sm text-[#8b949e]">Click to select or drag a file</p>}
          <p className="m-0 text-xs text-[#8b949e] mt-1">Max 5MB</p>
         </div>
        </div>

        {/* Upload Result */}
        {schoolUploadResult && (
         <div className={`p-4 rounded-xl border ${schoolUploadResult.success ? "bg-[#f0fdf4] border-[#bbf7d0]" : "bg-red-50 border-red-200"}`}>
          <p className={`m-0 text-sm font-semibold ${schoolUploadResult.success ? "text-[#16a34a]" : "text-red-700"}`}>
           {schoolUploadResult.success
            ? `Validated ${schoolUploadResult.schools?.length || 0} school(s) — will be saved on cycle creation`
            : schoolUploadResult.message || "Validation failed"}
          </p>
          {schoolUploadResult.errors?.length > 0 && (
           <div className="mt-3 max-h-40 overflow-y-auto space-y-1">
            {schoolUploadResult.errors.map((err, i) => (
             <p key={i} className="m-0 text-xs text-red-600">Row {err.row}: {err.message}</p>
            ))}
           </div>
          )}
         </div>
        )}
       </div>

       {/* Footer */}
       <div className="flex justify-end gap-3 px-6 py-4 border-t border-[#e5e7eb] bg-[#f6f8fa] rounded-b-lg">
        <button className="py-2.5 px-5 text-sm font-semibold rounded-md cursor-pointer transition-all duration-200 bg-white text-[#57606a] border border-[#d0d5dd] hover:bg-[#f3f4f6] disabled:opacity-50"
         onClick={() => setShowSchoolUploadModal(false)} disabled={schoolUploadLoading}>
         {schoolUploadResult?.success ? "Close" : "Cancel"}
        </button>
        {!schoolUploadResult?.success && (
         <button className="inline-flex items-center justify-center gap-2 py-2.5 px-5 text-sm font-semibold border-none rounded-md cursor-pointer transition-all duration-200 bg-[#0078d4] text-white hover:bg-[#106ebe] shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={async () => {
           if (!schoolUploadFile || schoolUploadLoading) return;
           setSchoolUploadLoading(true);
           setSchoolUploadResult(null);
           try {
            const res = await previewNonAdoptedSchools(schoolUploadFile, form.state_id, editingId);
            setSchoolUploadResult(res);
            if (res.success && res.schools?.length) {
             // Store in staging (NO DB writes yet)
             setStagedSchools((prev) => {
              const existingKeys = new Set(prev.map((s) => s.existing_id || `${s.name}_${s.udise_code}`));
              const newOnes = res.schools.filter((s) => {
               const key = s.existing_id || `${s.name}_${s.udise_code}`;
               return !existingKeys.has(key);
              });
              return [...prev, ...newOnes];
             });
             // Schools already in the DB are ticked straight away; ones already
             // selected (in_cycle, or a header-checkbox add) aren't counted again.
             const existingRows = res.schools.filter((s) => s.existing_id).map((s) => ({ ...s, id: s.existing_id }));
             if (existingRows.length > 0) setSchoolsSelected(existingRows, true);
             setToast({ type: "success", message: `Validated ${res.schools.length} school(s) — they will be saved when you create the cycle` });
            }
           } catch (err) {
            const detail = apiErrorMessage(err, "Upload failed");
            setSchoolUploadResult({ success: false, errors: [], message: detail });
            setToast({ message: detail, type: "error" });
           } finally {
            setSchoolUploadLoading(false);
           }
          }}
          disabled={!schoolUploadFile || schoolUploadLoading}>
          {schoolUploadLoading ? (
           <>
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
             <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
             <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Uploading…
           </>
          ) : (
           <><HiOutlineUpload className="text-base" /> Upload</>
          )}
         </button>
        )}
       </div>
      </div>
     </div>
    )}
   </div>
  );
 }

 // ── LIST VIEW (DEFAULT) ──
 return (
  <div className="p-6">
   {toast && <Toast {...toast} onClose={() => setToast(null)} />}

   {/* Table (title/subtitle come from the shared Header; search + filters +
       New Cycle live in its toolbar, same layout as Districts) */}
   <div className="bg-white rounded-lg border border-[#e5e7eb] overflow-x-auto">
    {/* Toolbar */}
    <div className="flex justify-between items-center px-6 py-4 border-b border-[#e5e7eb]">
     <div className="relative flex items-center flex-1 max-w-sm">
      <HiOutlineSearch className="absolute left-3.5 text-[#8b949e] text-sm" />
      <input
       type="text"
       className="w-full py-2.5 pr-8 pl-10 text-sm border border-[#d0d5dd] rounded-md text-[#24292f] bg-white transition-all duration-200 box-border placeholder:text-[#8b949e] focus:outline-none focus:border-[#0078d4] focus:ring-2 focus:ring-[#0078d4]/10"
       placeholder="Search cycles…"
       value={listSearch}
       onChange={(e) => setListSearch(e.target.value)}
      />
      {listSearch && (
       <button
        className="absolute right-2 bg-transparent border-none text-[#8b949e] cursor-pointer p-1 rounded-md hover:text-[#57606a] hover:bg-[#f3f4f6] transition-colors"
        onClick={() => setListSearch("")}
       >
        <HiOutlineX className="text-sm" />
       </button>
      )}
     </div>
     <div className="flex items-center gap-2">
      <FilterDropdown
       filters={[
        {
         key: "state_id",
         label: "State",
         type: "select",
         options: states.map((s) => ({ value: String(s.id), label: s.name })),
        },
        {
         key: "assessment_year_id",
         label: "Assessment Year",
         type: "select",
         options: assessmentYears.map((y) => ({ value: String(y.id), label: y.name })),
        },
        {
         key: "status",
         label: "Status",
         type: "select",
         options: [
          { value: "published", label: "Published" },
          { value: "unpublished", label: "Unpublished" },
          { value: "completed", label: "Completed" },
         ],
        },
       ]}
       values={{ state_id: filterState, assessment_year_id: filterYear, status: filterStatus }}
       onChange={(next) => {
        setFilterState(next.state_id || "");
        setFilterYear(next.assessment_year_id || "");
        setFilterStatus(next.status || "");
        setPage(1);
       }}
      />
      {canManageCycles && (
       <button
        onClick={openCreate}
        className="inline-flex items-center gap-2 h-10 px-5 text-sm font-semibold border-none rounded-xl cursor-pointer transition-all duration-200 bg-[#0078d4] text-white hover:bg-[#106ebe]"
       >
        <HiOutlinePlus className="text-base" /> New Cycle
       </button>
      )}
     </div>
    </div>
    <table className="w-full text-sm">
     <thead>
      <tr className="bg-[#f6f8fa] border-b border-[#e5e7eb]">
       <th className="text-left px-4 py-3 font-semibold text-[#24292f]">Title</th>
       <th className="text-left px-4 py-3 font-semibold text-[#24292f]">State</th>
       <th className="text-left px-4 py-3 font-semibold text-[#24292f]">Round</th>
       <th className="text-left px-4 py-3 font-semibold text-[#24292f]">Year</th>
       <th className="text-left px-4 py-3 font-semibold text-[#24292f]">Start</th>
       <th className="text-left px-4 py-3 font-semibold text-[#24292f]">End</th>
       <th className="text-left px-4 py-3 font-semibold text-[#24292f]">Status</th>
      </tr>
     </thead>
     <tbody>
      {loading ? (
       <tr><td colSpan={7} className="text-center py-12 text-[#8b949e]">Loading…</td></tr>
      ) : cycles.length === 0 ? (
       <tr><td colSpan={7} className="text-center py-12 text-[#8b949e]">No cycles found.</td></tr>
      ) : (
       cycles.map((c) => (
        <tr key={c.id} onClick={() => openEdit(c.id)} className="border-b border-[#f0f0f0] hover:bg-[#f6f8fa] transition-colors cursor-pointer">
         <td className="px-4 py-3 font-medium text-[#24292f]">{c.title || "—"}</td>
         <td className="px-4 py-3 text-[#57606a]">{c.state_name || c.state_id}</td>
         <td className="px-4 py-3 text-[#57606a]">{c.round_number ? `Round ${c.round_number}` : "—"}</td>
         <td className="px-4 py-3 text-[#57606a]">{c.assessment_year_name || "—"}</td>
         <td className="px-4 py-3 text-[#57606a]">{c.start_date}</td>
         <td className="px-4 py-3 text-[#57606a]">{c.end_date}</td>
         <td className="px-4 py-3">
          {c.is_completed ? (
           <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-700">Completed</span>
          ) : c.is_published ? (
           <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-700">Published</span>
          ) : (
           <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-yellow-100 text-yellow-700">Unpublished</span>
          )}
         </td>
        </tr>
       ))
      )}
     </tbody>
    </table>
   </div>

   {/* Pagination */}
   {!loading && totalCount > 0 && (
    <Pagination
     currentPage={page}
     totalPages={totalPages}
     totalCount={totalCount}
     pageSize={pageSize}
     onPageChange={setPage}
     onPageSizeChange={handlePageSizeChange}
     entityName="cycles"
    />
   )}

   {/* Delete confirm */}
   {showDeleteConfirm && (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
     <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-5 text-center">
      <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-red-100 flex items-center justify-center">
       <HiOutlineExclamation className="text-2xl text-red-600" />
      </div>
      <h3 className="text-base font-semibold text-[#24292f] mb-1">Delete Cycle?</h3>
      <p className="text-sm text-[#57606a] mb-4">
       Delete {showDeleteConfirm.title || showDeleteConfirm.cycle_type}? This cannot be undone.
      </p>
      <div className="flex justify-center gap-2">
       <button onClick={() => setShowDeleteConfirm(null)} className="px-4 py-2 text-sm font-medium text-[#24292f] bg-white border border-[#d0d7de] rounded-lg hover:bg-[#f3f4f6]">Cancel</button>
       <button onClick={() => handleDelete(showDeleteConfirm.id)} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700">Delete</button>
      </div>
     </div>
    </div>
   )}

   {/* Publish confirm */}
   {showPublishConfirm && (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
     <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-5 text-center">
      <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-green-100 flex items-center justify-center">
       <HiOutlineCheckCircle className="text-2xl text-green-600" />
      </div>
      <h3 className="text-base font-semibold text-[#24292f] mb-1">Publish Cycle?</h3>
      <p className="text-sm text-[#57606a] mb-4">
       Are you sure you want to publish {showPublishConfirm.title || showPublishConfirm.cycle_type}?
      </p>
      <div className="flex justify-center gap-2">
       <button onClick={() => setShowPublishConfirm(null)} className="px-4 py-2 text-sm font-medium text-[#24292f] bg-white border border-[#d0d7de] rounded-lg hover:bg-[#f3f4f6]">Cancel</button>
       <button onClick={() => handlePublish(showPublishConfirm.id)} className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700">Publish</button>
      </div>
     </div>
    </div>
   )}

   {/* Unpublish confirm */}
   {showUnpublishConfirm && (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
     <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-5 text-center">
      <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-yellow-100 flex items-center justify-center">
       <HiOutlineExclamation className="text-2xl text-yellow-600" />
      </div>
      <h3 className="text-base font-semibold text-[#24292f] mb-1">Unpublish Cycle?</h3>
      <p className="text-sm text-[#57606a] mb-4">
       Are you sure you want to unpublish {showUnpublishConfirm.title || showUnpublishConfirm.cycle_type}?
      </p>
      <div className="flex justify-center gap-2">
       <button onClick={() => setShowUnpublishConfirm(null)} className="px-4 py-2 text-sm font-medium text-[#24292f] bg-white border border-[#d0d7de] rounded-lg hover:bg-[#f3f4f6]">Cancel</button>
       <button onClick={() => handleUnpublish(showUnpublishConfirm.id)} className="px-4 py-2 text-sm font-medium text-white bg-yellow-500 rounded-lg hover:bg-yellow-600">Unpublish</button>
      </div>
     </div>
    </div>
   )}
  </div>
 );
}

export default Cycles;